import React, { useState, useCallback, Fragment, useEffect, useRef, useMemo } from "react";
import { Modal, Button, Row, Col, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Scatter } from 'react-chartjs-2';
import ClientAppShell from "../components/ClientAppShell";
import ContentLoader from "../components/ContentLoader";
import BusinessValueReport from "../components/BusinessValueReport";
import { fetchCapabilities, fetchSelectedCapabilities } from "../services/capabilitiesApi";
import { createOrUpdateBusinessValueProject, getBusinessValueProject, getBusinessValueProjectDetail, calculateCashFlow, getBusinessValueProjectsByUser } from "../services/businessValueCalculatorApi";
import { getSMEPendingProjectList } from "../services/smeApi";
import { generateReport, loadProjectDataForReport } from "../utils/reportGenerationUtils";
import { useAuth } from "../auth/AuthContext";
import { ArrowLeft, ArrowRight, BarChart, Calculator, CheckCircle2, ClipboardList, DollarSign, Eye, FileText, LineChart, List, Pencil, Plus, Save } from "lucide-react";
import "../assets/css/business-value-calculator.css";
// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Utility functions for number formatting
const formatNumberWithCommas = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return "";
  
  // Round to avoid floating point precision errors
  const roundedNum = Math.round(num * 1000000) / 1000000; // Round to 6 decimal places
  
  // Check if the number has meaningful decimal places after rounding
  const hasDecimals = Math.abs(roundedNum % 1) > 0.000001; // Only consider decimals if > 0.000001
  
  if (hasDecimals) {
    // If it has meaningful decimals, show them (up to 1 decimal place for currency)
    return roundedNum.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1
    });
  } else {
    // If it's effectively a whole number, show it without decimal
    return Math.round(roundedNum).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }
};

const parseFormattedNumber = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  // Remove commas and parse
  const cleanValue = value.replace(/,/g, '');
  const num = parseFloat(cleanValue);
  return isNaN(num) ? "" : num;
};

// Percentage input utility functions
const formatPercentage = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return ""; // Don't show 0%
  return num + "%"; // Add % symbol to the value
};

const parsePercentage = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  // Remove % sign and parse
  const cleanValue = value.replace(/%/g, '');
  const num = parseFloat(cleanValue);
  return isNaN(num) ? "" : num;
};

// Percentage Input Component
const PercentageInput = ({ 
  name, 
  value, 
  onChange, 
  placeholder, 
  disabled = false, 
  required = false,
  min = 0,
  max = 100,
  step = 0.1,
  className = "form-input"
}) => {
  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow typing % symbol, but don't require it
    let cleanValue = inputValue;
    
    // If user is typing and doesn't have % at the end, allow it
    if (inputValue && !inputValue.endsWith('%') && !inputValue.includes('%')) {
      // User is typing numbers, let them continue
      cleanValue = inputValue;
    } else {
      // User has % symbol, parse the numeric part
      cleanValue = inputValue.replace(/%/g, '');
    }
    
    const numericValue = parseFloat(cleanValue);
    
    // Validate max value - silently prevent input if exceeds max
    if (!isNaN(numericValue) && numericValue > max) {
      return; // Don't update if exceeds max
    }
    
    // Validate min value - silently prevent input if below min
    if (!isNaN(numericValue) && numericValue < min) {
      return; // Don't update if below min
    }
    
    // Call parent onChange with numeric value
    onChange({
      target: {
        name: name,
        value: isNaN(numericValue) ? "" : numericValue
      }
    });
  };

  const handleBlur = (e) => {
    // Ensure value is within bounds on blur and add % if missing
    const inputValue = e.target.value;
    const numericValue = parsePercentage(inputValue);
    
    if (numericValue !== "" && numericValue < min) {
      onChange({
        target: {
          name: name,
          value: min
        }
      });
    }
  };

  return (
    <div className="percentage-input-container">
      <input
        type="text"
        name={name}
        value={formatPercentage(value)}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={className}
        maxLength={7} // Allow for "100.00%" (7 characters)
      />
    </div>
  );
};

// Numeric Input Component (for decimal numbers without % symbol)
const NumericInput = ({ 
  name, 
  value, 
  onChange, 
  placeholder, 
  disabled = false, 
  required = false,
  min = 0,
  max = null,
  step = 0.1,
  className = "form-input",
  decimalPlaces = 2
}) => {
  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow only numbers, decimal point, and minus sign
    const cleanValue = inputValue.replace(/[^0-9.-]/g, '');
    
    // Prevent multiple decimal points
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      return;
    }
    
    // For validation, check if it's a valid number
    const numericValue = parseFloat(cleanValue);
    
    // Validate max value - silently prevent input if exceeds max
    if (!isNaN(numericValue) && max !== null && numericValue > max) {
      return;
    }
    
    // Validate min value - silently prevent input if below min
    if (!isNaN(numericValue) && numericValue < min) {
      return;
    }
    
    // Call parent onChange with the raw clean value to preserve decimal formatting
    onChange({
      target: {
        name: name,
        value: cleanValue === "" ? "" : cleanValue
      }
    });
  };

  const formatNumericValue = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    
    // If it's already a string with decimal formatting, preserve it
    if (typeof val === "string") {
      // Check if it's a valid number format
      const num = parseFloat(val);
      if (!isNaN(num)) {
        return val; // Return the original string to preserve formatting
      }
    }
    
    // If it's a number, convert to string
    const num = parseFloat(val);
    if (isNaN(num)) return "";
    return num.toString();
  };

  const handleBlur = (e) => {
    // Ensure value is within bounds on blur
    const inputValue = e.target.value;
    const numericValue = parseFloat(inputValue);
    
    if (numericValue !== "" && !isNaN(numericValue) && numericValue < min) {
      onChange({
        target: {
          name: name,
          value: min
        }
      });
    }
  };

  return (
    <div className="numeric-input-container">
      <input
        type="text"
        name={name}
        value={formatNumericValue(value)}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={className}
        maxLength={15} // Allow for longer decimal numbers
      />
    </div>
  );
};

// Chart data generation functions



const BusinessValueCalculator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const isTransitioningRef = useRef(false);
  const [capabilities, setCapabilities] = useState([]);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [visibleSection, setVisibleSection] = useState(null);
  const [projectDetails, setProjectDetails] = useState([]);
  const [pendingProjects, setPendingProjects] = useState([]);
  const [pendingProjectsLoading, setPendingProjectsLoading] = useState(false);
  const [pendingProjectsError, setPendingProjectsError] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [businessValueProjectId, setBusinessValueProjectId] = useState(0);
  const [userId, setUserId] = useState(null);
  const [cashFlowReport, setCashFlowReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const reportRef = useRef(null);
  const [currentProjectStatus, setCurrentProjectStatus] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [capabilitiesFromAPI, setCapabilitiesFromAPI] = useState(false); // Track if capabilities are loaded from API
  const [generatingReport, setGeneratingReport] = useState(false);
  const [projectSummaryData, setProjectSummaryData] = useState(null);
  
  // List view states
  const [showListView, setShowListView] = useState(true); // Default to list view
  const [projectsList, setProjectsList] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    totalRecords: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // State for formatted display values
  const [formattedValues, setFormattedValues] = useState({
    initialImplementationCost: "",
    annualRecurringCosts: "",
    modelCharges: "",
    licenseFees: "",
    maintenanceCosts: "",
    otherCosts: "",
    // Value Driver Responses - Revenue Growth
    revenueGrowthBaselineCost: "",
    revenueGrowthAIImprovement: "",
    revenueGrowthDerivedSavings: "",
    // Value Driver Responses - Cost Reduction
    costReductionBaselineCost: "",
    costReductionAIImprovement: "",
    costReductionDerivedSavings: "",
    // Value Driver Responses - Operational Efficiency
    operationalEfficiencyBaselineCost: "",
    operationalEfficiencyAIImprovement: "",
    operationalEfficiencyDerivedSavings: "",
    // Value Driver Responses - Risk & Compliance
    riskComplianceBaselineCost: "",
    riskComplianceAIImprovement: "",
    riskComplianceDerivedSavings: ""
  });

  // State for project type selection
  const [projectType, setProjectType] = useState(null); // null, 'existing', or 'new'
  const [showProjectTypeSelection, setShowProjectTypeSelection] = useState(false);
  const [selectedExistingProject, setSelectedExistingProject] = useState(null);
  const [isNewReportForExistingProject, setIsNewReportForExistingProject] = useState(false);

  // State for individual use case expansion (keeping for nested use cases)
  const [expandedUseCases, setExpandedUseCases] = useState({});

  // Function to toggle individual use case expansion with accordion behavior
  const toggleUseCase = (useCaseKey) => {
    setExpandedUseCases(prev => {
      // If the clicked use case is already open, close it
      if (prev[useCaseKey]) {
        return { [useCaseKey]: false };
      }
      // Otherwise, close all others and open only the clicked one
      return { [useCaseKey]: true };
    });
  };

  // Form data state
  const [formData, setFormData] = useState({
    // Project Scope and Objective
    projectName: "",
    projectDescription: "",
    projectObjective: [],
    dataAICapabilities: [],
    selectedCapabilities: [], // New structure for primary and sub-capabilities
    expectedDurationYear: "",
    expectedDurationMonth: "",
    projectIdentifier: "",
    
    // Business Value Drivers
    selectedValueDrivers: [],
    
    // Project Costs
    initialImplementationCost: "",
    annualRecurringCosts: "",
    modelCharges: "",
    licenseFees: "",
    maintenanceCosts: "",
    otherCosts: "",
    otherCostsDescription: "",
    
    // Value Driver Responses - Revenue Growth
    revenueGrowthBaselineCost: 0,
    revenueGrowthAIImprovement: "",
    revenueGrowthDerivedSavings: 0,
    
    // Value Driver Responses - Cost Reduction
    costReductionBaselineCost: 0,
    costReductionAIImprovement: "",
    costReductionDerivedSavings: 0,
    
    // Value Driver Responses - Operational Efficiency
    operationalEfficiencyBaselineCost: 0,
    operationalEfficiencyAIImprovement: "",
    operationalEfficiencyDerivedSavings: 0,
    
    // Value Driver Responses - Risk & Compliance
    riskComplianceBaselineCost: 0,
    riskComplianceAIImprovement: "",
    riskComplianceDerivedSavings: 0,
    
    // Final Section
    projectTerm: "",
    implementationEffort: "",
    adoptionRateOverTime: [],
    businessFunctionRelation: 0,
    sharedImpact: "",
    discountRate: "",
    // ROI Sensitivity Analysis
    roiSensitivityType: "",
    roiSensitivityRate: "",
    waccPercentage: "",
    annualSaving: "",
    expectedInitialImplementationCost: "",
    totalAnnualRecurringCosts: "",
    secondSensitivityField: "",
    secondSensitivityRate: "",
    // Custom values for first parameter
    firstParamValue: "",
    // Custom values for second parameter
    secondParamValue: ""
  });

  // Available options
  const projectObjectives = [
    "Increase revenue",
    "Reduce costs", 
    "Improve operations",
    "Mitigate risks",
    "Enhance customer experience",
    "Improve decision making",
    "Increase efficiency",
    "Digital transformation"
  ];

  // Format currency helper function
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Load project details from auth context
  useEffect(() => {
    try {
      if (user) {
        if (user.userId) {
          setUserId(user.userId);
        }

        if (user.projectDetails && Array.isArray(user.projectDetails)) {
          setProjectDetails(user.projectDetails);
          if (user.projectDetails.length > 0) {
            const firstProject = user.projectDetails[0];
            setSelectedProjectId(firstProject.projectId);
            setFormData(prev => ({
              ...prev,
              projectName: firstProject.projectName
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error loading project details:', error);
      toast.error("Failed to load project details");
    }
  }, [user]);

  // Fetch pending projects from SME API
  const fetchPendingProjects = useCallback(async () => {
    try {
      setPendingProjectsLoading(true);
      setPendingProjectsError(null);
      console.log("Fetching pending projects...");
      
      const response = await getSMEPendingProjectList();
      console.log("Full API response:", response);
      
      // Handle different possible response structures
      let projects = [];
      
      if (response) {
        if (response.projects && Array.isArray(response.projects)) {
          // Structure: { projects: [...], totalCount: 0 }
          projects = response.projects;
        } else if (Array.isArray(response)) {
          // Structure: [...] (direct array)
          projects = response;
        } else if (response.data && Array.isArray(response.data)) {
          // Structure: { data: [...], ... }
          projects = response.data;
        } else if (response.data && response.data.projects && Array.isArray(response.data.projects)) {
          // Structure: { data: { projects: [...], totalCount: 0 } }
          projects = response.data.projects;
        } else {
          console.warn("Unexpected response structure for pending projects:", response);
          setPendingProjectsError(`Unexpected response format. Expected array or object with projects/data property. Got: ${typeof response}`);
          setPendingProjects([]);
          return;
        }
      } else {
        console.warn("No response received from API");
        setPendingProjectsError("No response received from API");
        setPendingProjects([]);
        return;
      }
      
      setPendingProjects(projects);
      console.log("Pending projects loaded:", projects);
    } catch (error) {
      console.error("Error fetching pending projects:", error);
      setPendingProjects([]);
      setPendingProjectsError(error.message || "Failed to load pending projects");
      toast.error("Failed to load pending projects");
    } finally {
      setPendingProjectsLoading(false);
    }
  }, []);

  // Fetch pending projects on component mount
  useEffect(() => {
    fetchPendingProjects();
  }, [fetchPendingProjects]);

  // Fetch capabilities from API
  const fetchCapabilitiesData = useCallback(async () => {
    if (!selectedProjectId) return;
    
    try {
      setCapabilitiesLoading(true);
      const response = await fetchCapabilities(selectedProjectId);
      
      if (response && response.succeeded && response.data) {
        setCapabilities(response.data);
      } else {
        setCapabilities([]);
      }
    } catch (err) {
      console.error('Error fetching capabilities:', err);
      setCapabilities([]);
      toast.error("Failed to load capabilities");
    } finally {
      setCapabilitiesLoading(false);
    }
  }, [selectedProjectId]);

  // Fetch selected capabilities for existing project
  const fetchSelectedCapabilitiesData = useCallback(async () => {
    if (!selectedProjectId || !userId) return;
    
    try {
      const response = await fetchSelectedCapabilities({
        userId,
        projectId: selectedProjectId
      });
      
      if (response && response.succeeded && response.data && response.data.selectedCapabilities) {
        // Map the selected capabilities to the form data structure
        const mappedCapabilities = response.data.selectedCapabilities.map(capability => ({
          capabilityId: capability.capabilityId,
          selectedSubCapabilities: capability.selectedSubCapabilities.map(subCap => ({
            subCapabilityId: subCap.subCapabilityId,
            state: subCap.state,
            isNewLocked: subCap.isNewLocked
          }))
        }));
        
        // Update form data with selected capabilities
        setFormData(prev => ({
          ...prev,
          selectedCapabilities: mappedCapabilities
        }));
        
        // Mark capabilities as loaded from API (read-only)
        setCapabilitiesFromAPI(true);
        
        console.log("Selected capabilities loaded:", mappedCapabilities);
      }
    } catch (error) {
      console.error("Error fetching selected capabilities:", error);
    }
  }, [selectedProjectId, userId]);

  // Load capability catalog when a project context is available
  useEffect(() => {
    if (selectedProjectId) {
      fetchCapabilitiesData();
    }
  }, [selectedProjectId, fetchCapabilitiesData]);

  // Only lock capabilities from SME when user picks an existing pending project for a new report
  useEffect(() => {
    if (selectedProjectId && userId && projectType === "existing" && isNewReportForExistingProject) {
      fetchSelectedCapabilitiesData();
    }
  }, [selectedProjectId, userId, projectType, isNewReportForExistingProject, fetchSelectedCapabilitiesData]);

  // Toggle visibility for capability sections
  const toggleVisibility = (sectionId) => {
    setVisibleSection(visibleSection === sectionId ? null : sectionId);
    setSelectedCard(sectionId);
  };

  // Handle checkbox change for sub-capabilities
  const handleCheckboxChange = (e, capabilityId, subCapabilityId) => {
    const isChecked = e.target.checked;
    
    // If capabilities are loaded from API, make them read-only
    if (capabilitiesFromAPI) {
      return;
    }
    
    // Check if this sub-capability is locked or approved (from existing project data)
    const existingSubCapability = formData.selectedCapabilities
      ?.find(item => item.capabilityId === capabilityId)
      ?.selectedSubCapabilities
      ?.find(sub => sub.subCapabilityId === subCapabilityId);
    
    // If it's approved (state === "4") or locked, don't allow changes
    if (existingSubCapability && (existingSubCapability.state === "4" || existingSubCapability.isNewLocked)) {
      return;
    }
    
    setFormData(prev => {
      const currentSelectedCapabilities = prev.selectedCapabilities || [];
      
      // Find existing capability entry
      const capabilityIndex = currentSelectedCapabilities.findIndex(
        item => item.capabilityId === capabilityId
      );
      
      if (capabilityIndex !== -1) {
        // Capability exists, update its sub-capabilities
        const capability = currentSelectedCapabilities[capabilityIndex];
        const existingSubCapabilities = capability.selectedSubCapabilities || [];
        const subCapabilityIndex = existingSubCapabilities.findIndex(
          sub => sub.subCapabilityId === subCapabilityId
        );
        
        let updatedSubCapabilities;
        if (isChecked && subCapabilityIndex === -1) {
          // Add new sub-capability
          updatedSubCapabilities = [
            ...existingSubCapabilities,
            { 
              subCapabilityId,
              state: "3", // New selection state
              isNewLocked: false
            }
          ];
        } else if (!isChecked && subCapabilityIndex !== -1) {
          // Only allow removal if not approved or locked
          const subCap = existingSubCapabilities[subCapabilityIndex];
          if (subCap.state === "4" || subCap.isNewLocked) {
            return prev; // Don't allow removal of approved/locked items
          }
          
          updatedSubCapabilities = existingSubCapabilities.filter(
            (_, index) => index !== subCapabilityIndex
          );
        } else {
          // No change needed
          return prev;
        }
        
        // Update the capability entry
        const updatedCapabilities = [...currentSelectedCapabilities];
        if (updatedSubCapabilities.length === 0) {
          // Remove capability if no sub-capabilities remain
          updatedCapabilities.splice(capabilityIndex, 1);
        } else {
          updatedCapabilities[capabilityIndex] = {
            ...capability,
            selectedSubCapabilities: updatedSubCapabilities
          };
        }
        
        return {
          ...prev,
          selectedCapabilities: updatedCapabilities
        };
      } else if (isChecked) {
        // Capability doesn't exist, create new entry
        return {
          ...prev,
          selectedCapabilities: [
            ...currentSelectedCapabilities,
            {
              capabilityId,
              selectedSubCapabilities: [{ 
                subCapabilityId,
                state: "3", // New selection state
                isNewLocked: false
              }]
            }
          ]
        };
      }
      
      return prev;
    });
  };

  // Load project data specifically for report generation
  const loadProjectDataForReport = useCallback(async (projectId) => {
    if (!projectId || capabilities.length === 0) return null;
    
    try {
      const response = await getBusinessValueProject(projectId);
      
      if (response) {
        // Handle both response structures - with .data wrapper or direct response
        const data = response.data || response;
        
        // Set the business value project ID
        if (data.id) {
          setBusinessValueProjectId(data.id);
        }
        
        // Extract project objectives from the response structure
        const extractedProjectObjectives = data.projectObjectives && Array.isArray(data.projectObjectives) ? 
          data.projectObjectives.map(obj => obj.projectObjective).filter(obj => obj && projectObjectives.includes(obj)) : [];
        
        // Calculate annual recurring costs
        const modelCharges = data.modelCharges || 0;
        const licenseFees = data.licenseFees || 0;
        const maintenanceCosts = data.maintenanceCosts || 0;
        const otherCosts = data.otherCosts || 0;
        const calculatedAnnualRecurringCosts = modelCharges + licenseFees + maintenanceCosts + otherCosts;

        // Initialize value driver data
        const valueDriverData = {
          revenueGrowthBaselineCost: 0,
          revenueGrowthAIImprovement: "",
          revenueGrowthDerivedSavings: 0,
          costReductionBaselineCost: 0,
          costReductionAIImprovement: "",
          costReductionDerivedSavings: 0,
          operationalEfficiencyBaselineCost: 0,
          operationalEfficiencyAIImprovement: "",
          operationalEfficiencyDerivedSavings: 0,
          riskComplianceBaselineCost: 0,
          riskComplianceAIImprovement: "",
          riskComplianceDerivedSavings: 0
        };

        // Load value driver data if available
        if (data.valueDriverResponses && data.valueDriverResponses.length > 0) {
          data.valueDriverResponses.forEach(response => {
            const { driverType, baselineAnnualCost, aiImprovement, derivedSavings } = response;
            
            switch (driverType) {
              case "Revenue Growth":
                valueDriverData.revenueGrowthBaselineCost = baselineAnnualCost || 0;
                valueDriverData.revenueGrowthAIImprovement = aiImprovement && aiImprovement > 0 ? aiImprovement.toString() : "";
                valueDriverData.revenueGrowthDerivedSavings = derivedSavings || 0;
                break;
              case "Cost Reduction":
                valueDriverData.costReductionBaselineCost = baselineAnnualCost || 0;
                valueDriverData.costReductionAIImprovement = aiImprovement && aiImprovement > 0 ? aiImprovement.toString() : "";
                valueDriverData.costReductionDerivedSavings = derivedSavings || 0;
                break;
              case "Operational Efficiency":
                valueDriverData.operationalEfficiencyBaselineCost = baselineAnnualCost || 0;
                valueDriverData.operationalEfficiencyAIImprovement = aiImprovement && aiImprovement > 0 ? aiImprovement.toString() : "";
                valueDriverData.operationalEfficiencyDerivedSavings = derivedSavings || 0;
                break;
              case "Risk & Compliance":
                valueDriverData.riskComplianceBaselineCost = baselineAnnualCost || 0;
                valueDriverData.riskComplianceAIImprovement = aiImprovement && aiImprovement > 0 ? aiImprovement.toString() : "";
                valueDriverData.riskComplianceDerivedSavings = derivedSavings || 0;
                break;
            }
          });
        }

        // Build the form data update object
        const formDataUpdate = {
          projectName: data.projectName || "",
          projectDescription: data.projectDescription || "",
          projectObjective: extractedProjectObjectives,
          expectedDurationYear: data.expectedDurationYears || "",
          expectedDurationMonth: data.expectedDurationMonths || "",
          selectedValueDrivers: data.businessValueDrivers ? 
            data.businessValueDrivers.map(driver => driver.driverType) :
            (data.valueDriverResponses ? 
              [...new Set(data.valueDriverResponses.map(response => response.driverType))] : []),
          initialImplementationCost: data.initialImplementationCost ? data.initialImplementationCost.toString() : "",
          annualRecurringCosts: calculatedAnnualRecurringCosts > 0 ? calculatedAnnualRecurringCosts.toString() : "",
          modelCharges: modelCharges > 0 ? modelCharges.toString() : "",
          licenseFees: licenseFees > 0 ? licenseFees.toString() : "",
          maintenanceCosts: maintenanceCosts > 0 ? maintenanceCosts.toString() : "",
          otherCosts: otherCosts > 0 ? otherCosts.toString() : "",
          otherCostsDescription: data.otherCostsDescription || "",
          discountRate: data.discountRate && data.discountRate > 0 ? data.discountRate.toString() : "",
          sharedImpact: data.sharedImpact !== null && data.sharedImpact !== undefined ? data.sharedImpact.toString() : "",
          roiSensitivityType: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityType : "",
          roiSensitivityRate: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityRate.toString() : "",
          // Set sensitivity analysis fields from senstivityAnalysis array
          firstParamValue: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityValue.toString() : "",
          secondSensitivityField: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityType : "",
          secondParamValue: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityValue.toString() : "",
          secondSensitivityRate: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityRate.toString() : "",
          // Set adoption rates
          adoptionRateOverTime: data.adoptionRates ? data.adoptionRates.map(rate => rate.adoptionPercentage.toString()) : [],
          // Set selected capabilities
          selectedCapabilities: data.selectedCapabilities ? 
            data.selectedCapabilities.map(capabilityItem => ({
              capabilityId: capabilityItem.capabilityId,
              selectedSubCapabilities: capabilityItem.selectedSubCapabilities || []
            })) : [],
          dataAICapabilities: data.dataAICapabilities ? 
            data.dataAICapabilities.map(cap => {
              const matchedCapability = capabilities.find(c => c.id === cap.capabilityId);
              return matchedCapability ? matchedCapability.capabilityName : null;
            }).filter(name => name !== null) : [],
          // Add value driver data
          ...valueDriverData
        };
        
        // Set the form data
        setFormData(prev => ({
          ...prev,
          ...formDataUpdate
        }));
        
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error loading project data for report:', error);
      return null;
    }
  }, [capabilities, projectObjectives]);

  // Load saved business value project data
  const loadSavedBusinessValueData = useCallback(async (preserveReadOnlyState = false) => {
    if (!businessValueProjectId || capabilities.length === 0) return;
    
    // Don't load existing data if this is a new report for an existing project
    if (isNewReportForExistingProject) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await getBusinessValueProject(businessValueProjectId);
      
      if (response) {
        // Handle both response structures - with .data wrapper or direct response
        const data = response.data || response;
        // Only set the business value project ID if it's different to prevent loops
        if (data.id && data.id !== businessValueProjectId) {
          setBusinessValueProjectId(data.id);
        }
        
        // Set project status and read-only state
        const projectStatus = data.saveStage || 'Draft';
        setCurrentProjectStatus(projectStatus);
        // Only set read-only state if not preserving existing state and not a new project
        if (!preserveReadOnlyState && projectType !== 'new') {
          setIsReadOnly(projectStatus === 'Complete');
        } else {
          console.log('Preserving existing read-only state during data load or new project');
        }
        
        // Set current step - always start at step 1 in view mode
        if (isReadOnly) {
          // In view mode, always start at step 1
          setCurrentStep(1);
          // Mark all steps as completed since the project is complete
          setCompletedSteps([1, 2, 3, 4, 5, 6]);
        } else {
          // In edit mode, use the saved current step
          // Only update step if we're not in the middle of a transition
          if (!isTransitioningRef.current) {
            if (data.currentStep) {
              setCurrentStep(data.currentStep);
              // Mark previous steps as completed
              const completedStepsArray = [];
              for (let i = 1; i < data.currentStep; i++) {
                completedStepsArray.push(i);
              }
              setCompletedSteps(completedStepsArray);
            } else {
              // Default to step 1 if no current step is saved
              setCurrentStep(1);
              setCompletedSteps([]);
            }
          }
        }
        
        // Populate form data with saved values
        const modelCharges = data.modelCharges || 0;
        const licenseFees = data.licenseFees || 0;
        const maintenanceCosts = data.maintenanceCosts || 0;
        const otherCosts = data.otherCosts || 0;
        const calculatedAnnualRecurringCosts = modelCharges + licenseFees + maintenanceCosts + otherCosts;
        
        // Extract project objectives from the response structure
        const extractedProjectObjectives = data.projectObjectives && Array.isArray(data.projectObjectives) ? 
          data.projectObjectives.map(obj => obj.projectObjective).filter(obj => obj && projectObjectives.includes(obj)) : [];
        
        // Debug: Log sensitivity analysis data
        console.log('🔍 Loading sensitivity analysis data:', {
          senstivityType: data.senstivityType,
          senstivityRate: data.senstivityRate,
          senstivityAnalysis: data.senstivityAnalysis
        });
        
        const formDataUpdate = {
          projectName: data.projectName || "",
          projectDescription: data.projectDescription || "",
          projectObjective: extractedProjectObjectives,
          expectedDurationYear: data.expectedDurationYears || "",
          expectedDurationMonth: data.expectedDurationMonths || "",
          initialImplementationCost: data.initialImplementationCost ? data.initialImplementationCost.toString() : "",
          annualRecurringCosts: calculatedAnnualRecurringCosts > 0 ? calculatedAnnualRecurringCosts.toString() : "",
          modelCharges: modelCharges > 0 ? modelCharges.toString() : "",
          licenseFees: licenseFees > 0 ? licenseFees.toString() : "",
          maintenanceCosts: maintenanceCosts > 0 ? maintenanceCosts.toString() : "",
          otherCosts: otherCosts > 0 ? otherCosts.toString() : "",
          otherCostsDescription: data.otherCostsDescription || "",
          implementationEffort: data.implementationEffortRating && data.implementationEffortRating > 0 ? data.implementationEffortRating.toString() : "",
          businessFunctionRelation: data.crossFunctionalRelationPercentage || 0,
          sharedImpact: data.sharedImpact !== null && data.sharedImpact !== undefined ? data.sharedImpact.toString() : "",
          discountRate: data.discountRate && data.discountRate > 0 ? data.discountRate.toString() : "",
          // Set adoption rates
          adoptionRateOverTime: data.adoptionRates ? data.adoptionRates.map(rate => rate.adoptionPercentage.toString()) : [],
          // Set selected value drivers from businessValueDrivers or valueDriverResponses
          selectedValueDrivers: data.businessValueDrivers ? 
            data.businessValueDrivers.map(driver => driver.driverType) :
            (data.valueDriverResponses ? 
              [...new Set(data.valueDriverResponses.map(response => response.driverType))] : []),
        // Set data AI capabilities from dataAICapabilities
        dataAICapabilities: data.dataAICapabilities ? 
          data.dataAICapabilities.map(cap => {
            // Find the capability name by matching capabilityId with the fetched capabilities
            const matchedCapability = capabilities.find(c => c.id === cap.capabilityId);
            return matchedCapability ? matchedCapability.capabilityName : null;
            }).filter(name => name !== null) : [],
        // Set selectedCapabilities from API response (nested structure)
        selectedCapabilities: data.selectedCapabilities ? 
          data.selectedCapabilities.map(capabilityItem => ({
            capabilityId: capabilityItem.capabilityId,
            selectedSubCapabilities: capabilityItem.selectedSubCapabilities || []
          })) : 
          // Fallback: if dataAICapabilities exists but selectedCapabilities doesn't, try to reconstruct it
          (data.dataAICapabilities && data.dataAICapabilities.length > 0) ? 
            data.dataAICapabilities.reduce((acc, cap) => {
              const existingCap = acc.find(c => c.capabilityId === cap.capabilityId);
              if (existingCap) {
                existingCap.selectedSubCapabilities.push({ subCapabilityId: cap.subCapabilityId });
              } else {
                acc.push({
                  capabilityId: cap.capabilityId,
                  selectedSubCapabilities: [{ subCapabilityId: cap.subCapabilityId }]
                });
              }
              return acc;
            }, []) : [],
          // Map additional properties from the response
          projectIdentifier: data.projectIdentifier || "",
          projectTerm: data.projectTermValue ? data.projectTermValue.toString() : "",
          // ROI Sensitivity Analysis - will be set after value driver data is loaded
        };
        
        setFormData(prev => {
          const newFormData = {
            ...prev,
            ...formDataUpdate
          };
          
          // Debug: Log the sensitivity analysis values being set
          console.log('🔍 Setting sensitivity analysis form data:', {
            roiSensitivityType: newFormData.roiSensitivityType,
            roiSensitivityRate: newFormData.roiSensitivityRate,
            firstParamValue: newFormData.firstParamValue,
            secondSensitivityField: newFormData.secondSensitivityField,
            secondParamValue: newFormData.secondParamValue,
            secondSensitivityRate: newFormData.secondSensitivityRate
          });
          
          return newFormData;
        });

        // Update formatted values for display
        setFormattedValues({
          initialImplementationCost: formatNumberWithCommas(formDataUpdate.initialImplementationCost),
          annualRecurringCosts: formatNumberWithCommas(formDataUpdate.annualRecurringCosts),
          modelCharges: formatNumberWithCommas(formDataUpdate.modelCharges),
          licenseFees: formatNumberWithCommas(formDataUpdate.licenseFees),
          maintenanceCosts: formatNumberWithCommas(formDataUpdate.maintenanceCosts),
          otherCosts: formatNumberWithCommas(formDataUpdate.otherCosts),
          expectedRevenueIncrease: formatNumberWithCommas(formDataUpdate.expectedRevenueIncrease || ""),
          customerBaseExpansionRevenue: formatNumberWithCommas(formDataUpdate.customerBaseExpansionRevenue || ""),
          newProductsRevenue: formatNumberWithCommas(formDataUpdate.newProductsRevenue || ""),
          headcountReductionSavings: formatNumberWithCommas(formDataUpdate.headcountReductionSavings || ""),
          processAutomationSavings: formatNumberWithCommas(formDataUpdate.processAutomationSavings || ""),
          overheadReductionSavings: formatNumberWithCommas(formDataUpdate.overheadReductionSavings || ""),
          operationalCostReduction: formatNumberWithCommas(formDataUpdate.operationalCostReduction || ""),
          productivityIncreaseSavings: formatNumberWithCommas(formDataUpdate.productivityIncreaseSavings || ""),
          laborCostAutomationSavings: formatNumberWithCommas(formDataUpdate.laborCostAutomationSavings || ""),
          regulatoryPenaltyReduction: formatNumberWithCommas(formDataUpdate.regulatoryPenaltyReduction || ""),
          securityIncidentPrevention: formatNumberWithCommas(formDataUpdate.securityIncidentPrevention || ""),
          riskExposureReduction: formatNumberWithCommas(formDataUpdate.riskExposureReduction || "")
        });
        
        // Set value driver responses
        if (data.valueDriverResponses && data.valueDriverResponses.length > 0) {
          const responses = data.valueDriverResponses;
          
          // Initialize all value driver fields
          const valueDriverData = {
            revenueGrowthBaselineCost: 0,
            revenueGrowthAIImprovement: "",
            revenueGrowthDerivedSavings: 0,
            costReductionBaselineCost: 0,
            costReductionAIImprovement: "",
            costReductionDerivedSavings: 0,
            operationalEfficiencyBaselineCost: 0,
            operationalEfficiencyAIImprovement: "",
            operationalEfficiencyDerivedSavings: 0,
            riskComplianceBaselineCost: 0,
            riskComplianceAIImprovement: "",
            riskComplianceDerivedSavings: 0
          };
          
          // Map responses by driver type
          responses.forEach(response => {
            const { driverType, baselineAnnualCost, aiImprovement, derivedSavings } = response;
            
            switch (driverType) {
              case "Revenue Growth":
                valueDriverData.revenueGrowthBaselineCost = baselineAnnualCost || 0;
                valueDriverData.revenueGrowthAIImprovement = aiImprovement && aiImprovement > 0 ? aiImprovement : "";
                valueDriverData.revenueGrowthDerivedSavings = derivedSavings || 0;
                break;
              case "Cost Reduction":
                valueDriverData.costReductionBaselineCost = baselineAnnualCost || 0;
                valueDriverData.costReductionAIImprovement = aiImprovement && aiImprovement > 0 ? aiImprovement : "";
                valueDriverData.costReductionDerivedSavings = derivedSavings || 0;
                break;
              case "Operational Efficiency":
                valueDriverData.operationalEfficiencyBaselineCost = baselineAnnualCost || 0;
                valueDriverData.operationalEfficiencyAIImprovement = aiImprovement && aiImprovement > 0 ? aiImprovement : "";
                valueDriverData.operationalEfficiencyDerivedSavings = derivedSavings || 0;
                break;
              case "Risk & Compliance":
                valueDriverData.riskComplianceBaselineCost = baselineAnnualCost || 0;
                valueDriverData.riskComplianceAIImprovement = aiImprovement && aiImprovement > 0 ? aiImprovement : "";
                valueDriverData.riskComplianceDerivedSavings = derivedSavings || 0;
                break;
            }
          });
          
          // Update form data with all value driver responses and sensitivity analysis
          setFormData(prev => {
            const newFormData = {
              ...prev,
              ...valueDriverData,
              // ROI Sensitivity Analysis - set after value driver data is loaded
              roiSensitivityType: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityType : "",
              roiSensitivityRate: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityRate.toString() : "",
              // Set sensitivity analysis fields from senstivityAnalysis array
              firstParamValue: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityValue.toString() : "",
              secondSensitivityField: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityType : "",
              secondParamValue: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityValue.toString() : "",
              secondSensitivityRate: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityRate.toString() : ""
            };
            
            // Debug: Log the sensitivity analysis values being set after value driver data
            console.log('🔍 Setting sensitivity analysis form data (after value driver data):', {
              roiSensitivityType: newFormData.roiSensitivityType,
              roiSensitivityRate: newFormData.roiSensitivityRate,
              firstParamValue: newFormData.firstParamValue,
              secondSensitivityField: newFormData.secondSensitivityField,
              secondParamValue: newFormData.secondParamValue,
              secondSensitivityRate: newFormData.secondSensitivityRate
            });
            
            return newFormData;
          });
          
          // Update formatted values for display
          setFormattedValues(prev => ({
            ...prev,
            revenueGrowthBaselineCost: formatNumberWithCommas(valueDriverData.revenueGrowthBaselineCost),
            revenueGrowthDerivedSavings: formatNumberWithCommas(valueDriverData.revenueGrowthDerivedSavings),
            costReductionBaselineCost: formatNumberWithCommas(valueDriverData.costReductionBaselineCost),
            costReductionDerivedSavings: formatNumberWithCommas(valueDriverData.costReductionDerivedSavings),
            operationalEfficiencyBaselineCost: formatNumberWithCommas(valueDriverData.operationalEfficiencyBaselineCost),
            operationalEfficiencyDerivedSavings: formatNumberWithCommas(valueDriverData.operationalEfficiencyDerivedSavings),
            riskComplianceBaselineCost: formatNumberWithCommas(valueDriverData.riskComplianceBaselineCost),
            riskComplianceDerivedSavings: formatNumberWithCommas(valueDriverData.riskComplianceDerivedSavings)
          }));
        } else {
          // If no value driver responses, still set sensitivity analysis data
          setFormData(prev => {
            const newFormData = {
              ...prev,
              // ROI Sensitivity Analysis
              roiSensitivityType: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityType : "",
              roiSensitivityRate: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityRate.toString() : "",
              // Set sensitivity analysis fields from senstivityAnalysis array
              firstParamValue: data.senstivityAnalysis && data.senstivityAnalysis.length > 0 ? data.senstivityAnalysis[0].senstivityValue.toString() : "",
              secondSensitivityField: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityType : "",
              secondParamValue: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityValue.toString() : "",
              secondSensitivityRate: data.senstivityAnalysis && data.senstivityAnalysis.length > 1 ? data.senstivityAnalysis[1].senstivityRate.toString() : ""
            };
            
            // Debug: Log the sensitivity analysis values being set (no value driver data)
            console.log('🔍 Setting sensitivity analysis form data (no value driver data):', {
              roiSensitivityType: newFormData.roiSensitivityType,
              roiSensitivityRate: newFormData.roiSensitivityRate,
              firstParamValue: newFormData.firstParamValue,
              secondSensitivityField: newFormData.secondSensitivityField,
              secondParamValue: newFormData.secondParamValue,
              secondSensitivityRate: newFormData.secondSensitivityRate
            });
            
            return newFormData;
          });
        }
      }
    } catch (err) {
      console.error('Error loading saved business value data:', err);
      // Don't show error toast as this is expected for new projects
    } finally {
      setLoading(false);
    }
  }, [businessValueProjectId, isNewReportForExistingProject, projectType]); // Remove capabilities from dependencies to prevent infinite loops

  // Load saved data when businessValueProjectId and capabilities are available - Fixed to prevent infinite loops
  useEffect(() => {
    if (businessValueProjectId && capabilities.length > 0) {
      loadSavedBusinessValueData();
    }
  }, [businessValueProjectId, capabilities.length]); // Use capabilities.length instead of capabilities array and loadSavedBusinessValueData

  // Debug read-only state changes
  useEffect(() => {
  }, [isReadOnly]);

  // Debug formData changes
  useEffect(() => {
  }, [formData.selectedCapabilities, formData.dataAICapabilities]);


  // Auto-generate adoption rate periods based on project duration
  useEffect(() => {
    const years = parseFloat(formData.expectedDurationYear) || 0;
    const months = parseFloat(formData.expectedDurationMonth) || 0;
    
    if (years > 0 || months > 0) {
      // Calculate number of periods: years + months (e.g., 5 years + 3 months = 8 periods)
      const numberOfPeriods = years + months;
      
      // Only update if the number of periods has changed
      if (formData.adoptionRateOverTime.length !== numberOfPeriods) {
        setFormData(prev => ({
          ...prev,
          adoptionRateOverTime: Array(numberOfPeriods).fill("")
        }));
      }
    }
  }, [formData.expectedDurationYear, formData.expectedDurationMonth]);

  // Calculate derived savings when baseline cost or AI improvement changes
  useEffect(() => {
    const calculateDerivedSavings = (baselineCost, aiImprovement) => {
      const cost = parseFloat(baselineCost) || 0;
      const improvement = parseFloat(aiImprovement) || 0;
      return cost * (improvement / 100);
    };

    // Calculate for each value driver
    const revenueGrowthDerivedSavings = calculateDerivedSavings(
      formData.revenueGrowthBaselineCost, 
      formData.revenueGrowthAIImprovement
    );
    
    const costReductionDerivedSavings = calculateDerivedSavings(
      formData.costReductionBaselineCost, 
      formData.costReductionAIImprovement
    );
    
    const operationalEfficiencyDerivedSavings = calculateDerivedSavings(
      formData.operationalEfficiencyBaselineCost, 
      formData.operationalEfficiencyAIImprovement
    );
    
    const riskComplianceDerivedSavings = calculateDerivedSavings(
      formData.riskComplianceBaselineCost, 
      formData.riskComplianceAIImprovement
    );

    // Update form data with calculated derived savings
    setFormData(prev => ({
      ...prev,
      revenueGrowthDerivedSavings,
      costReductionDerivedSavings,
      operationalEfficiencyDerivedSavings,
      riskComplianceDerivedSavings
    }));

    // Update formatted values for display
    setFormattedValues(prev => ({
      ...prev,
      revenueGrowthDerivedSavings: formatNumberWithCommas(revenueGrowthDerivedSavings),
      costReductionDerivedSavings: formatNumberWithCommas(costReductionDerivedSavings),
      operationalEfficiencyDerivedSavings: formatNumberWithCommas(operationalEfficiencyDerivedSavings),
      riskComplianceDerivedSavings: formatNumberWithCommas(riskComplianceDerivedSavings)
    }));
  }, [
    formData.revenueGrowthBaselineCost,
    formData.revenueGrowthAIImprovement,
    formData.costReductionBaselineCost,
    formData.costReductionAIImprovement,
    formData.operationalEfficiencyBaselineCost,
    formData.operationalEfficiencyAIImprovement,
    formData.riskComplianceBaselineCost,
    formData.riskComplianceAIImprovement
  ]);

  const businessValueDrivers = [
    "Revenue Growth",
    "Cost Reduction", 
    "Operational Efficiency",
    "Risk & Compliance"
  ];


  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  // Handle back button click to go to project type selection or list view
  const handleBackToProjects = () => {
    if (projectType !== null) {
      // If user is in form (both edit and view mode), go back to list view
      setShowListView(true);
      setShowReport(false);
      setProjectType(null);
      setSelectedExistingProject(null);
      setIsNewReportForExistingProject(false);
      setCapabilitiesFromAPI(false);
      setCurrentStep(1);
    } else {
      // If user is in project type selection, go to list view
      setShowListView(true);
      setShowReport(false);
    }
  };

  // Fetch projects list for list view
  const fetchProjectsList = useCallback(async (page = 1, pageSize = 10) => {
    try {
      setProjectsLoading(true);
      setProjectsError(null);
      
      if (!userId) {
        console.error("No userId available for fetching projects");
        setProjectsError("User not authenticated");
        setProjectsList([]);
        return;
      }
      
      const response = await getBusinessValueProjectsByUser(userId, page, pageSize);
      
      // Handle the new API response structure
      if (response && response.data && response.data.data) {
        setProjectsList(response.data.data);
        // Store pagination info from the nested data structure
        setPaginationInfo({
          totalRecords: response.data.totalRecords || 0,
          pageNumber: response.data.pageNumber || 1,
          pageSize: response.data.pageSize || 10,
          totalPages: response.data.totalPages || 0,
          hasNextPage: response.data.hasNextPage || false,
          hasPreviousPage: response.data.hasPreviousPage || false
        });
      } else {
        setProjectsList([]);
        setPaginationInfo({
          totalRecords: 0,
          pageNumber: 1,
          pageSize: 10,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false
        });
      }
    } catch (error) {
      console.error("Error fetching projects list:", error);
      setProjectsError("Failed to load projects");
      setProjectsList([]);
    } finally {
      setProjectsLoading(false);
    }
  }, [userId]);

  // Fetch projects list when list view is shown
  useEffect(() => {
    if (showListView) {
      fetchProjectsList();
    }
  }, [showListView, fetchProjectsList]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    // Prevent decimal input for years field
    if (name === 'expectedDurationYear') {
      // Only allow whole numbers - remove any decimal points or non-numeric characters except empty string
      const cleanValue = value.replace(/[^0-9]/g, '');
      if (cleanValue !== value) {
        e.target.value = cleanValue;
        setFormData(prev => ({
          ...prev,
          [name]: cleanValue === '' ? '' : parseInt(cleanValue) || 0
        }));
        return;
      }
    }
    
    if (name === 'projectObjective') {
      // Legacy multi-select path (objectives use per-checkbox handlers in the UI)
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData(prev => ({
        ...prev,
        [name]: selectedOptions
      }));
    } else if (name === 'projectName') {
      // Text field updates only — existing-project <select> uses its own onChange handler
      setFormData(prev => ({
        ...prev,
        projectName: value
      }));
    } else if (name === 'adoptionRateOverTime') {
      // Handle adoption rate array - this will be handled separately
      return;
    } else {
      setFormData(prev => {
        const newFormData = {
          ...prev,
          [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : 
            (name === 'expectedDurationYear' ? (parseInt(value) || 0) : (parseFloat(value) || 0))) : value)
        };
        
        // Auto-calculate Annual Recurring Costs when cost breakdown fields change
        if (['modelCharges', 'licenseFees', 'maintenanceCosts', 'otherCosts'].includes(name)) {
          const modelCharges = name === 'modelCharges' ? (parseFloat(value) || 0) : (parseFloat(prev.modelCharges) || 0);
          const licenseFees = name === 'licenseFees' ? (parseFloat(value) || 0) : (parseFloat(prev.licenseFees) || 0);
          const maintenanceCosts = name === 'maintenanceCosts' ? (parseFloat(value) || 0) : (parseFloat(prev.maintenanceCosts) || 0);
          const otherCosts = name === 'otherCosts' ? (parseFloat(value) || 0) : (parseFloat(prev.otherCosts) || 0);
          
          const totalAnnualRecurringCosts = modelCharges + licenseFees + maintenanceCosts + otherCosts;
          newFormData.annualRecurringCosts = totalAnnualRecurringCosts > 0 ? totalAnnualRecurringCosts : "";
        }
        
        return newFormData;
      });
    }
  }, []);

  // Client's registered projects (from auth) — used for "Existing Project" dropdown
  const existingProjectOptions = useMemo(() => {
    const fromAuth = (projectDetails || []).map((p) => ({
      projectId: p.projectId,
      projectName: p.projectName
    }));
    const seen = new Set(fromAuth.map((p) => p.projectId));
    const fromPending = (pendingProjects || []).filter((p) => p?.projectId && !seen.has(p.projectId));
    return [...fromAuth, ...fromPending];
  }, [projectDetails, pendingProjects]);

  const handleAdoptionRateChange = useCallback((index, value) => {
    setFormData(prev => {
      const newAdoptionRates = [...prev.adoptionRateOverTime];
      newAdoptionRates[index] = value === "" ? "" : (parseFloat(value) || "");
      return {
        ...prev,
        adoptionRateOverTime: newAdoptionRates
      };
    });
  }, []);

  // Handler for formatted number inputs
  const handleFormattedNumberChange = useCallback((fieldName, displayValue) => {
    // Remove any non-numeric characters except decimal point
    const cleanValue = displayValue.replace(/[^0-9.]/g, '');
    
    // Check if the number exceeds maximum digits (10 digits before decimal point for 32-bit int max)
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    
    // Limit to 10 digits before decimal point (2,147,483,647 = 10 digits)
    if (integerPart && integerPart.length > 10) {
      return; // Don't update if exceeds limit
    }
    
    // Limit to 1 decimal place for currency fields
    if (decimalPart && decimalPart.length > 1) {
      return; // Don't update if exceeds decimal limit
    }
    
    // Parse the numeric value
    const numericValue = cleanValue === "" ? "" : (parseFloat(cleanValue) || "");
    
    // Check if value exceeds 32-bit signed integer maximum (2,147,483,647)
    if (numericValue !== "" && numericValue > 2147483647) {
      return; // Don't update if exceeds 32-bit int max
    }
    
    // Format the display value with commas
    const formattedDisplayValue = numericValue === "" ? "" : formatNumberWithCommas(numericValue);
    
    // Update formatted display value
    setFormattedValues(prev => ({
      ...prev,
      [fieldName]: formattedDisplayValue
    }));
    
    // Update form data with numeric value
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [fieldName]: numericValue
      };
      
      // Auto-calculate Annual Recurring Costs when cost breakdown fields change
      if (['modelCharges', 'licenseFees', 'maintenanceCosts', 'otherCosts'].includes(fieldName)) {
        const modelCharges = fieldName === 'modelCharges' ? numericValue : (parseFloat(prev.modelCharges) || 0);
        const licenseFees = fieldName === 'licenseFees' ? numericValue : (parseFloat(prev.licenseFees) || 0);
        const maintenanceCosts = fieldName === 'maintenanceCosts' ? numericValue : (parseFloat(prev.maintenanceCosts) || 0);
        const otherCosts = fieldName === 'otherCosts' ? numericValue : (parseFloat(prev.otherCosts) || 0);
        
        const totalAnnualRecurringCosts = modelCharges + licenseFees + maintenanceCosts + otherCosts;
        newFormData.annualRecurringCosts = totalAnnualRecurringCosts;
        
        // Update formatted value for annual recurring costs
        setFormattedValues(prevFormatted => ({
          ...prevFormatted,
          annualRecurringCosts: totalAnnualRecurringCosts > 0 ? formatNumberWithCommas(totalAnnualRecurringCosts) : ""
        }));
      }
      
      return newFormData;
    });
  }, []);



  const validateStep = useCallback((step) => {
    switch (step) {
      case 1:
        return formData.projectName && formData.projectDescription && formData.projectObjective.length > 0 && formData.expectedDurationYear && formData.expectedDurationYear > 0;
      case 2:
        return formData.selectedValueDrivers.length > 0;
      case 3:
        return formData.initialImplementationCost !== "" && formData.initialImplementationCost > 0;
      case 4:
        // Validate based on selected value drivers
        const hasRevenueGrowth = formData.selectedValueDrivers.includes('Revenue Growth');
        const hasCostReduction = formData.selectedValueDrivers.includes('Cost Reduction');
        const hasOperationalEfficiency = formData.selectedValueDrivers.includes('Operational Efficiency');
        const hasRiskCompliance = formData.selectedValueDrivers.includes('Risk & Compliance');
        
        let isValid = true;
        if (hasRevenueGrowth) {
          isValid = isValid && (formData.revenueGrowthBaselineCost > 0 && formData.revenueGrowthAIImprovement !== "" && formData.revenueGrowthAIImprovement > 0);
        }
        if (hasCostReduction) {
          isValid = isValid && (formData.costReductionBaselineCost > 0 && formData.costReductionAIImprovement !== "" && formData.costReductionAIImprovement > 0);
        }
        if (hasOperationalEfficiency) {
          isValid = isValid && (formData.operationalEfficiencyBaselineCost > 0 && formData.operationalEfficiencyAIImprovement !== "" && formData.operationalEfficiencyAIImprovement > 0);
        }
        if (hasRiskCompliance) {
          isValid = isValid && (formData.riskComplianceBaselineCost > 0 && formData.riskComplianceAIImprovement !== "" && formData.riskComplianceAIImprovement > 0);
        }
        return isValid;
      case 5:
        // Validate implementation effort, discount rate, and adoption rates
        const hasImplementationEffort = formData.implementationEffort !== "" && formData.implementationEffort > 0;
        const hasDiscountRate = formData.discountRate !== "" && formData.discountRate > 0;
        
        // Validate adoption rates - ALL periods must have a value > 0
        const hasAdoptionRates = formData.adoptionRateOverTime.length > 0 && 
          formData.adoptionRateOverTime.every(rate => rate !== "" && parseFloat(rate) > 0);
        
        return hasImplementationEffort && hasDiscountRate && hasAdoptionRates;
      case 6:
        // Validate ROI sensitivity analysis
        const hasSensitivityType = formData.roiSensitivityType !== "";
        const hasFirstParamValue = formData.firstParamValue !== "" && parseFloat(formData.firstParamValue) >= 0;
        const hasFirstSensitivityRate = formData.roiSensitivityRate !== "" && parseFloat(formData.roiSensitivityRate) > 0;
        const hasSecondSensitivityField = formData.secondSensitivityField !== "";
        const hasSecondParamValue = formData.secondParamValue !== "" && parseFloat(formData.secondParamValue) >= 0;
        const hasSecondSensitivityRate = formData.secondSensitivityRate !== "" && parseFloat(formData.secondSensitivityRate) > 0;
        
        return hasSensitivityType && hasFirstParamValue && hasFirstSensitivityRate &&
               hasSecondSensitivityField && hasSecondParamValue && hasSecondSensitivityRate;
      default:
        return true;
    }
  }, [formData]);

  // Get validation error message for specific step
  const getValidationErrorMessage = useCallback((step) => {
    switch (step) {
      case 1:
        if (!formData.projectName) return "Project name is required";
        if (!formData.projectDescription) return "Project description is required";
        if (formData.projectObjective.length === 0) return "At least one project objective is required";
        if (!formData.expectedDurationYear || formData.expectedDurationYear <= 0) return "Project duration is required";
        return "Please complete all required fields";
      case 2:
        return "Please select at least one business value driver";
      case 3:
        return "Implementation cost is required";
      case 4:
        return "Please complete all selected value driver fields";
      case 5:
        //if (!formData.implementationEffort || formData.implementationEffort <= 0) return "Implementation effort rating is required";
        if (!formData.discountRate || formData.discountRate <= 0) return "Cost of Capital/Discount Rate (WACC%) is required";
        if (!formData.adoptionRateOverTime.length || !formData.adoptionRateOverTime.every(rate => rate !== "" && parseFloat(rate) > 0)) {
          return "All adoption rate periods are required";
        }
        // return "Please complete all required fields";
      case 6:
        if (!formData.roiSensitivityType) return "Please select the first parameter";
        if (!formData.firstParamValue || parseFloat(formData.firstParamValue) < 0) return "First parameter variation amount is required";
        if (!formData.roiSensitivityRate || parseFloat(formData.roiSensitivityRate) <= 0) return "First parameter sensitivity rate is required";
        if (!formData.secondSensitivityField) return "Please select the second parameter";
        if (!formData.secondParamValue || parseFloat(formData.secondParamValue) < 0) return "Second parameter variation amount is required";
        if (!formData.secondSensitivityRate || parseFloat(formData.secondSensitivityRate) <= 0) return "Second parameter sensitivity rate is required";
        return "Please complete all required fields";
      default:
        return "Please complete all required fields";
    }
  }, [formData]);

  // Transform form data to cash flow API format
  const transformFormDataToCashFlowFormat = useCallback(() => {
    // Calculate total annual savings from all value driver responses
    const totalAnnualSavings = 
      (formData.revenueGrowthDerivedSavings || 0) +
      (formData.costReductionDerivedSavings || 0) +
      (formData.operationalEfficiencyDerivedSavings || 0) +
      (formData.riskComplianceDerivedSavings || 0);

    // Build adoption rates with year and month information
    const adoptionRates = formData.adoptionRateOverTime.map((rate, index) => {
      const years = parseInt(formData.expectedDurationYear) || 0;
      const isYearPeriod = index < years;
      
      return {
        periodType: isYearPeriod ? "Year" : "Month",
        periodNumber: index + 1,
        year: isYearPeriod ? index + 1 : null,
        month: isYearPeriod ? null : (index - years + 1),
        adoptionPercentage: rate === "" ? 0 : parseFloat(rate) || 0
      };
    });

    // Build selected capabilities structure
    const selectedCapabilities = (formData.selectedCapabilities || []).map(capability => {
      // Handle both old and new capability structures
      const capabilityName = capability.capabilityName || capability.name || "";
      const subCapabilities = (capability.selectedSubCapabilities || []).map(subCap => ({
        subCapabilityName: subCap.subCapabilityName || subCap.name || ""
      }));
      
      return {
        capabilityName,
        selectedSubCapabilities: subCapabilities
      };
    });

    // Calculate ROI baseline
    const implementationCost = parseFloat(formData.initialImplementationCost) || 0;
    const annualRecurringCosts = parseFloat(formData.annualRecurringCosts) || 0;
    const projectTerm = parseInt(formData.expectedDurationYear) || 0;
    const sharedImpact = parseFloat(formData.sharedImpact) || 0;
    
    // Calculate net annual cash flow
    const netAnnualCashFlow = totalAnnualSavings - annualRecurringCosts;
    const adjustedCashFlow = netAnnualCashFlow * (1 - sharedImpact);
    
    // Calculate ROI baseline: (Total Benefits - Total Costs) / Total Costs * 100
    const totalSavings = adjustedCashFlow * projectTerm;
    const roiBaseline = implementationCost > 0 ? ((totalSavings - implementationCost) / implementationCost) * 100 : 0;

    const cashFlowData = {
      id: 0,
      businessValueProjectId: businessValueProjectId,
      projectTitle: formData.projectName || "",
      projectDescription: formData.projectDescription || "",
      totalAnnualSavings: totalAnnualSavings,
      initialImplementationCost: implementationCost,
      annualRecurringCosts: annualRecurringCosts,
      modelCharges: parseFloat(formData.modelCharges) || 0,
      licenseFees: parseFloat(formData.licenseFees) || 0,
      maintenanceCosts: parseFloat(formData.maintenanceCosts) || 0,
      otherCosts: parseFloat(formData.otherCosts) || 0,
      discountRate: parseFloat(formData.discountRate) || 0,
      projectTermValue: projectTerm,
      sharedImpact: sharedImpact,
      sensitivityRate: parseFloat(formData.roiSensitivityRate) || 0,
      roiBaseline: roiBaseline,
      selectedBusinessValueDrivers: formData.selectedValueDrivers || [],
      projectObjectives: formData.projectObjective || [],
      adoptionRates: adoptionRates,
      selectedCapabilities: selectedCapabilities,
      senstivityAnalysis: [
        {
          senstivityType: formData.roiSensitivityType || "",
          senstivityValue: parseFloat(formData.firstParamValue) || 0,
          senstivityRate: parseFloat(formData.roiSensitivityRate) || 0,
          upDownValue: parseFloat(formData.firstParamValue) || 0
        },
        {
          senstivityType: formData.secondSensitivityField || "",
          senstivityValue: parseFloat(formData.secondParamValue) || 0,
          senstivityRate: parseFloat(formData.secondSensitivityRate) || 0,
          upDownValue: parseFloat(formData.secondParamValue) || 0
        }
      ]
    };

    return cashFlowData;
  }, [formData, businessValueProjectId]);

  // Transform form data to API format
  const transformFormDataToApiFormat = useCallback(() => {
    // Get selected capability IDs from selectedCapabilities structure
    const selectedCapabilityIds = formData.selectedCapabilities?.map(item => item.capabilityId) || [];
    
    // Also maintain backward compatibility with dataAICapabilities
    const legacyCapabilityIds = (formData.dataAICapabilities || []).map(capabilityName => {
      const capability = capabilities.find(cap => cap.capabilityName === capabilityName);
      return capability ? capability.id : 0;
    }).filter(id => id > 0);
    
    // Combine both approaches, prioritizing selectedCapabilities
    const allCapabilityIds = [...new Set([...selectedCapabilityIds, ...legacyCapabilityIds])];

    // Build value driver responses
    const valueDriverResponses = [];
    
    // Revenue Growth response
    if (formData.selectedValueDrivers.includes('Revenue Growth') && 
        formData.revenueGrowthBaselineCost > 0 && 
        formData.revenueGrowthAIImprovement !== "" && 
        formData.revenueGrowthAIImprovement > 0) {
      valueDriverResponses.push({
        driverType: "Revenue Growth",
        baselineAnnualCost: parseFloat(formData.revenueGrowthBaselineCost) || 0,
        aiImprovement: parseFloat(formData.revenueGrowthAIImprovement) || 0,
        derivedSavings: parseFloat(formData.revenueGrowthDerivedSavings) || 0
      });
    }

    // Cost Reduction response
    if (formData.selectedValueDrivers.includes('Cost Reduction') && 
        formData.costReductionBaselineCost > 0 && 
        formData.costReductionAIImprovement !== "" && 
        formData.costReductionAIImprovement > 0) {
      valueDriverResponses.push({
        driverType: "Cost Reduction",
        baselineAnnualCost: parseFloat(formData.costReductionBaselineCost) || 0,
        aiImprovement: parseFloat(formData.costReductionAIImprovement) || 0,
        derivedSavings: parseFloat(formData.costReductionDerivedSavings) || 0
      });
    }

    // Operational Efficiency response
    if (formData.selectedValueDrivers.includes('Operational Efficiency') && 
        formData.operationalEfficiencyBaselineCost > 0 && 
        formData.operationalEfficiencyAIImprovement !== "" && 
        formData.operationalEfficiencyAIImprovement > 0) {
      valueDriverResponses.push({
        driverType: "Operational Efficiency",
        baselineAnnualCost: parseFloat(formData.operationalEfficiencyBaselineCost) || 0,
        aiImprovement: parseFloat(formData.operationalEfficiencyAIImprovement) || 0,
        derivedSavings: parseFloat(formData.operationalEfficiencyDerivedSavings) || 0
      });
    }

    // Risk & Compliance response
    if (formData.selectedValueDrivers.includes('Risk & Compliance') && 
        formData.riskComplianceBaselineCost > 0 && 
        formData.riskComplianceAIImprovement !== "" && 
        formData.riskComplianceAIImprovement > 0) {
      valueDriverResponses.push({
        driverType: "Risk & Compliance",
        baselineAnnualCost: parseFloat(formData.riskComplianceBaselineCost) || 0,
        aiImprovement: parseFloat(formData.riskComplianceAIImprovement) || 0,
        derivedSavings: parseFloat(formData.riskComplianceDerivedSavings) || 0
      });
    }

    // Build adoption rates
    const adoptionRates = formData.adoptionRateOverTime.map((rate, index) => {
      const years = parseFloat(formData.expectedDurationYear) || 0;
      const isYearPeriod = index < years;
      
      return {
        periodType: isYearPeriod ? "Year" : "Month",
        periodNumber: index + 1,
        year: isYearPeriod ? index + 1 : 0,
        month: isYearPeriod ? 0 : index - years + 1,
        adoptionPercentage: rate === "" ? 0 : (parseFloat(rate) || 0)
      };
    });

    const apiData = {
      userId: userId || 0,
      projectId: selectedProjectId || 0,
      businessValueProjectId: businessValueProjectId,
      saveStage: "Complete",
      currentStep: currentStep,
      projectName: formData.projectName,
      projectDescription: formData.projectDescription,
      expectedDurationYears: parseInt(formData.expectedDurationYear) || 0,
      expectedDurationMonths: parseInt(formData.expectedDurationMonth) || 0,
      projectObjectives: formData.projectObjective && formData.projectObjective.length > 0 ? formData.projectObjective : [],
      selectedCapabilities: formData.selectedCapabilities || [], // Include the detailed capability selections
      selectedBusinessValueDrivers: formData.selectedValueDrivers,
      initialImplementationCost: parseFloat(formData.initialImplementationCost) || 0,
      annualRecurringCosts: parseFloat(formData.annualRecurringCosts) || 0,
      modelCharges: parseFloat(formData.modelCharges) || 0,
      licenseFees: parseFloat(formData.licenseFees) || 0,
      maintenanceCosts: parseFloat(formData.maintenanceCosts) || 0,
      otherCosts: parseFloat(formData.otherCosts) || 0,
      otherCostsDescription: formData.otherCostsDescription,
      valueDriverResponses: valueDriverResponses,
      discountRate: parseFloat(formData.discountRate) || 0,
      projectTermValue: Math.round((parseInt(formData.expectedDurationYear) || 0) + ((parseInt(formData.expectedDurationMonth) || 0) / 12)),
      implementationEffortRating: formData.implementationEffort !== "" ? parseInt(formData.implementationEffort) : 1,
      crossFunctionalRelationPercentage: parseFloat(formData.businessFunctionRelation) || 0,
      sharedImpact: parseFloat(formData.sharedImpact) || 0,
      senstivityType: formData.roiSensitivityType || "",
      senstivityRate: parseFloat(formData.roiSensitivityRate) || 0,
      adoptionRates: adoptionRates,
      senstivityAnalysis: [
        {
          senstivityType: formData.roiSensitivityType || "",
          senstivityValue: parseFloat(formData.firstParamValue) || 0,
          senstivityRate: parseFloat(formData.roiSensitivityRate) || 0,
          upDownValue: parseFloat(formData.firstParamValue) || 0
        },
        {
          senstivityType: formData.secondSensitivityField || "",
          senstivityValue: parseFloat(formData.secondParamValue) || 0,
          senstivityRate: parseFloat(formData.secondSensitivityRate) || 0,
          upDownValue: parseFloat(formData.secondParamValue) || 0
        }
      ]
    };
    

    
    return apiData;
  }, [formData, capabilities, userId, selectedProjectId, businessValueProjectId, currentStep]);

  // Save as draft function
  const saveAsDraft = useCallback(async () => {
    try {
      const apiData = transformFormDataToApiFormat();
      
      // Determine save stage based on completion status
      // If all 6 steps are completed, save as "Complete", otherwise "Draft"
      const allStepsCompleted = completedSteps.length === 6 && completedSteps.includes(1) && 
                               completedSteps.includes(2) && completedSteps.includes(3) && 
                               completedSteps.includes(4) && completedSteps.includes(5) && 
                               completedSteps.includes(6);
      
      apiData.saveStage = allStepsCompleted ? "Complete" : "Draft";
      

      const response = await createOrUpdateBusinessValueProject(apiData);
      
      if (response && (response.succeeded || (response.data && response.data.success))) {
        if (response.data && response.data.businessValueProjectId) {
          setBusinessValueProjectId(response.data.businessValueProjectId);
        }
        const message = apiData.saveStage === "Complete" ? "Project completed and saved successfully!" : "Draft saved successfully!";
        toast.success(message);
      }
    } catch (err) {
      console.error('Error saving:', err);
      // Don't show error toast for draft saves to avoid interrupting user flow
    }
  }, [transformFormDataToApiFormat, completedSteps]);

  // Auto-save when step 6 (ROI Sensitivity) is completed - Fixed to prevent infinite loops
  useEffect(() => {
    // Only auto-save if we're on step 6, not in read-only mode, and all sensitivity fields are completed
    if (currentStep === 6 && !isReadOnly && 
        formData.roiSensitivityType && formData.roiSensitivityType !== "" &&
        formData.firstParamValue && formData.roiSensitivityRate &&
        formData.secondSensitivityField && formData.secondSensitivityField !== "" &&
        formData.secondParamValue && formData.secondSensitivityRate) {
      
      // Mark step 6 as completed
      if (!completedSteps.includes(6)) {
        setCompletedSteps(prev => [...prev, 6]);
      }
      
      // Call saveAsDraft directly without including it in dependencies
      const performAutoSave = async () => {
        try {
          const apiData = transformFormDataToApiFormat();
          const allStepsCompleted = completedSteps.length === 6 && completedSteps.includes(1) && 
                                   completedSteps.includes(2) && completedSteps.includes(3) && 
                                   completedSteps.includes(4) && completedSteps.includes(5) && 
                                   completedSteps.includes(6);
          
          apiData.saveStage = allStepsCompleted ? "Complete" : "Draft";
          
          const response = await createOrUpdateBusinessValueProject(apiData);
          
          if (response && response.succeeded) {
            if (response.data && response.data.id) {
              setBusinessValueProjectId(response.data.id);
            }
            console.log('Auto-saved business value project successfully');
          }
        } catch (error) {
          console.error('Error auto-saving business value project:', error);
        }
      };
      
      performAutoSave();
    }
  }, [currentStep, isReadOnly, formData.roiSensitivityType, formData.firstParamValue, formData.roiSensitivityRate, formData.secondSensitivityField, formData.secondParamValue, formData.secondSensitivityRate, completedSteps, transformFormDataToApiFormat]); // Remove saveAsDraft from dependencies

  const nextStep = useCallback(async () => {
    // Prevent multiple simultaneous step transitions
    if (isTransitioningRef.current) {
      console.log('Step transition already in progress, skipping...');
      return;
    }
    
    // For read-only projects, allow navigation without validation
    if (isReadOnly) {
      isTransitioningRef.current = true;
      setCurrentStep(prev => Math.min(prev + 1, 6));
      setTimeout(() => { isTransitioningRef.current = false; }, 100);
      return;
    }
    
    if (validateStep(currentStep)) {
      isTransitioningRef.current = true;
      
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }
      
      // Move to next step first to prevent navigation issues
      setCurrentStep(prev => Math.min(prev + 1, 6));
      
      // Save as draft after navigation (non-blocking)
      try {
        await saveAsDraft();
      } catch (error) {
        console.error('Error saving draft after navigation:', error);
        // Don't block navigation if save fails
      }
      
      // Reset transition flag after a short delay
      setTimeout(() => { isTransitioningRef.current = false; }, 100);
    } else {
      toast.error(getValidationErrorMessage(currentStep));
    }
  }, [currentStep, validateStep, completedSteps, saveAsDraft, isReadOnly, getValidationErrorMessage]);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      projectName: "",
      projectDescription: "",
      projectObjective: [],
      dataAICapabilities: [],
      expectedDurationYear: "",
      expectedDurationMonth: "",
      projectIdentifier: "",
      selectedValueDrivers: [],
      initialImplementationCost: "",
      annualRecurringCosts: "",
      modelCharges: "",
      licenseFees: "",
      maintenanceCosts: "",
      otherCosts: "",
      otherCostsDescription: "",
      // Value Driver Responses - Revenue Growth
      revenueGrowthBaselineCost: 0,
      revenueGrowthAIImprovement: "",
      revenueGrowthDerivedSavings: 0,
      // Value Driver Responses - Cost Reduction
      costReductionBaselineCost: 0,
      costReductionAIImprovement: "",
      costReductionDerivedSavings: 0,
      // Value Driver Responses - Operational Efficiency
      operationalEfficiencyBaselineCost: 0,
      operationalEfficiencyAIImprovement: "",
      operationalEfficiencyDerivedSavings: 0,
      // Value Driver Responses - Risk & Compliance
      riskComplianceBaselineCost: 0,
      riskComplianceAIImprovement: "",
      riskComplianceDerivedSavings: 0,
      projectTerm: "",
      implementationEffort: "",
      adoptionRateOverTime: [],
      businessFunctionRelation: 0,
      discountRate: "",
      // ROI Sensitivity Analysis
      roiSensitivityType: "",
      roiSensitivityRate: "",
      waccPercentage: "",
      annualSaving: "",
      expectedInitialImplementationCost: "",
      totalAnnualRecurringCosts: "",
      secondSensitivityField: "",
      secondSensitivityRate: "",
      // Custom values for first parameter
      firstParamValue: "",
      // Custom values for second parameter
      secondParamValue: ""
    });
    setCurrentStep(1);
    setCompletedSteps([]);
    setBusinessValueProjectId(0);
    
    // Reset project type selection
    setProjectType(null);
    setShowProjectTypeSelection(false);
    setSelectedExistingProject(null);
    setIsNewReportForExistingProject(false);
    
    // Reset summary view
    setShowSummary(false);
    
    // Reset read-only state
    setIsReadOnly(false);
    
    // Reset capabilities from API flag
    setCapabilitiesFromAPI(false);
    
    // Reset formatted values
    setFormattedValues({
      initialImplementationCost: "",
      annualRecurringCosts: "",
      modelCharges: "",
      licenseFees: "",
      maintenanceCosts: "",
      otherCosts: "",
      // Value Driver Responses - Revenue Growth
      revenueGrowthBaselineCost: "",
      revenueGrowthAIImprovement: "",
      revenueGrowthDerivedSavings: "",
      // Value Driver Responses - Cost Reduction
      costReductionBaselineCost: "",
      costReductionAIImprovement: "",
      costReductionDerivedSavings: "",
      // Value Driver Responses - Operational Efficiency
      operationalEfficiencyBaselineCost: "",
      operationalEfficiencyAIImprovement: "",
      operationalEfficiencyDerivedSavings: "",
      // Value Driver Responses - Risk & Compliance
      riskComplianceBaselineCost: "",
      riskComplianceAIImprovement: "",
      riskComplianceDerivedSavings: ""
    });
  }, []);

  // Function to get projects from auth user
  const getProjectsFromUser = useCallback(() => {
    return user?.projectDetails || [];
  }, [user]);

  // Function to handle project type selection
  const handleProjectTypeSelection = useCallback((type) => {
    setProjectType(type);
    setShowProjectTypeSelection(false);
    
    if (type === 'existing') {
      setSelectedExistingProject(null);
      setIsNewReportForExistingProject(false);
      setIsReadOnly(false);
      setCapabilitiesFromAPI(false);
      setFormData(prev => ({
        ...prev,
        projectName: '',
        projectObjective: [],
        selectedCapabilities: []
      }));
      setCurrentStep(1);
    } else if (type === 'new') {
      // Show new project form
      setIsNewReportForExistingProject(false);
      setIsReadOnly(false);
      setCapabilitiesFromAPI(false);
      setSelectedExistingProject(null);
      setFormData(prev => ({
        ...prev,
        projectName: '',
        projectObjective: [],
        selectedCapabilities: []
      }));
      setCurrentStep(1);
    }
  }, []);

  // Function to handle existing project selection
  const handleExistingProjectSelection = useCallback(async (project) => {
    setSelectedExistingProject(project);
    setIsNewReportForExistingProject(true);
    setIsReadOnly(false);
    setCapabilitiesFromAPI(false);
    setFormData(prev => ({
      ...prev,
      projectName: project.projectName,
      projectId: project.projectId
    }));
    setSelectedProjectId(project.projectId);
    
    // Load selected capabilities for the existing project
    if (project.projectId && userId) {
      try {
        const response = await fetchSelectedCapabilities({
          userId,
          projectId: project.projectId
        });
        
        if (response && response.succeeded && response.data && response.data.selectedCapabilities) {
          // Map the selected capabilities to the form data structure
          const mappedCapabilities = response.data.selectedCapabilities.map(capability => ({
            capabilityId: capability.capabilityId,
            selectedSubCapabilities: capability.selectedSubCapabilities.map(subCap => ({
              subCapabilityId: subCap.subCapabilityId,
              state: subCap.state,
              isNewLocked: subCap.isNewLocked
            }))
          }));
          
          // Update form data with selected capabilities
          setFormData(prev => ({
            ...prev,
            selectedCapabilities: mappedCapabilities
          }));
          
          // Mark capabilities as loaded from API (read-only)
          setCapabilitiesFromAPI(true);
          
          console.log("Selected capabilities loaded for existing project:", mappedCapabilities);
        }
      } catch (error) {
        console.error("Error fetching selected capabilities for existing project:", error);
      }
    }
  }, [userId]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Validate all steps first
    for (let i = 1; i <= 5; i++) {
      if (!validateStep(i)) {
        toast.error(`Step ${i}: ${getValidationErrorMessage(i)}`);
        return;
      }
    }

    // If on step 6, fetch project data before showing summary
    if (currentStep === 6 && businessValueProjectId) {
      try {
        setLoading(true);
        const response = await getBusinessValueProject(businessValueProjectId);
        console.log('🔍 API Response:', response);
        
        // Handle different response structures
        const projectData = response?.data || response;
        if (projectData) {
          setProjectSummaryData(projectData);
          
          // If the response doesn't include cashFlowCalculationResult, try to calculate it
          if (!projectData.cashFlowCalculationResult) {
            console.log('🔍 No cashFlowCalculationResult found, attempting to calculate...');
            try {
              const cashFlowData = transformFormDataToCashFlowFormat();
              cashFlowData.businessValueProjectId = businessValueProjectId;
              
              const cashFlowResponse = await calculateCashFlow(cashFlowData);
              if (cashFlowResponse && cashFlowResponse.data) {
                // Merge the cash flow result into the project data
                const updatedProjectData = {
                  ...projectData,
                  cashFlowCalculationResult: cashFlowResponse.data
                };
                setProjectSummaryData(updatedProjectData);
                console.log('🔍 Cash flow calculated and merged:', updatedProjectData);
              }
            } catch (cashFlowError) {
              console.error('Error calculating cash flow:', cashFlowError);
              // Continue with the original project data
            }
          }
        }
        setShowSummary(true);
      } catch (error) {
        console.error('Error fetching project details:', error);
        toast.error('Failed to fetch project details');
        // Still show summary with current form data
        setShowSummary(true);
      } finally {
        setLoading(false);
      }
    } else {
      // Show summary view instead of direct submission
      setShowSummary(true);
    }
  }, [validateStep, getValidationErrorMessage, currentStep, businessValueProjectId, transformFormDataToCashFlowFormat]);

  const handleFinalSubmit = useCallback(async () => {
    try {
      setLoading(true);
      
      const apiData = transformFormDataToApiFormat();
      const response = await createOrUpdateBusinessValueProject(apiData);
      
      if (response && response.data && (response.data.success || response.succeeded)) {
        const projectId = response.data && response.data.businessValueProjectId ? 
          response.data.businessValueProjectId : businessValueProjectId;
        
        if (projectId) {
          setBusinessValueProjectId(projectId);
          
          try {
            const cashFlowData = transformFormDataToCashFlowFormat();
            cashFlowData.businessValueProjectId = projectId;
            
            const cashFlowResponse = await calculateCashFlow(cashFlowData);
            
            if (cashFlowResponse && cashFlowResponse.data) {
              setCashFlowReport(cashFlowResponse.data);
              setShowReport(true);
              setShowSummary(false);
              toast.success("Business Value Calculator saved and report generated successfully!");
              // Don't reset form when report is successfully generated
            } else {
              toast.error("Report generation failed, but data was saved successfully");
              resetForm();
            }
          } catch (cashFlowError) {
            toast.error("Report generation failed, but data was saved successfully");
            resetForm();
          }
        } else {
          toast.success("Business Value Calculator saved successfully!");
          resetForm();
        }
      } else {
        toast.error(response?.message || "Failed to save Business Value Calculator");
      }
      
    } catch (err) {
      toast.error(err.message || "Failed to submit Business Value Calculator");
    } finally {
      setLoading(false);
    }
  }, [transformFormDataToApiFormat, transformFormDataToCashFlowFormat, businessValueProjectId, resetForm]);

  // Render project type selection
  const renderProjectTypeSelection = () => (
    <>
      {/* Back Button - Outside container */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '20px' }}>
        <button
          type="button"
          className="action-button secondary"
          onClick={handleBackToProjects}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' ,marginTop:'10px'}}
        >
          <ArrowLeft /> Back
        </button>
      </div>
      
      <div className="project-type-selection-container">
        <div className="project-type-selection-header">
          <h4>Choose Project</h4>
          <p>Select how you want to create your project</p>
        </div>
      <div className="project-type-buttons">
        <button
          className="action-button secondary"
          onClick={() => handleProjectTypeSelection('existing')}
        >
          <List />
          Existing Project
        </button>
        <button
          className="action-button primary"
          onClick={() => handleProjectTypeSelection('new')}
        >
          <Plus />
          New Project
        </button>
      </div>
      </div>
    </>
  );


  const renderStep1 = () => (
    <div className="step-content">
      <h5 className="step-title">
        <ClipboardList className="icon" />
        Project Scope and Objective
      </h5>
      
      <div className="form-row">
        <div className="form-col-6">
          <div className="form-group">
            <label className="form-label required">Project Name</label>
            {projectType === 'existing' ? (
              <div>
                {selectedExistingProject ? (
                  <input
                    type="text"
                    name="projectName"
                    value={formData.projectName}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    className="form-input"
                    placeholder="Enter project name"
                  />
                ) : (
                  <div>
                    <select
                      name="projectName"
                      value={formData.projectName}
                      onChange={(e) => {
                        const selectedProject = existingProjectOptions.find(
                          (p) => p.projectName === e.target.value
                        );
                        if (selectedProject) {
                          handleExistingProjectSelection(selectedProject);
                        } else {
                          setFormData(prev => ({ ...prev, projectName: e.target.value }));
                        }
                      }}
                      required
                      disabled={isReadOnly || pendingProjectsLoading}
                      className="form-select"
                    >
                      <option value="">
                        {pendingProjectsLoading
                          ? "Loading projects..."
                          : existingProjectOptions.length === 0
                            ? "No projects available"
                            : "Select Existing Project"
                        }
                      </option>
                      {existingProjectOptions.map(project => (
                        <option key={project.projectId} value={project.projectName}>
                          {project.projectName}
                        </option>
                      ))}
                    </select>
                    {existingProjectOptions.length === 0 && !pendingProjectsLoading && (
                      <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                        No projects found on your account. Choose &quot;New Project&quot; instead.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <input
                type="text"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                required
                disabled={isReadOnly}
                placeholder="Enter project name"
                className="form-input"
              />
            )}
          </div>
        </div>
        <div className="form-col-6">
          <div className="form-group">
            <label className="form-label required">Expected Duration</label>
            <div className="duration-inputs-container">
              <div className="duration-input-group">
                <input
                  type="number"
                  name="expectedDurationYear"
                  value={formData.expectedDurationYear}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    // Prevent decimal point, minus sign, and 'e' from being typed
                    if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                      e.preventDefault();
                    }
                  }}
                  required
                  disabled={isReadOnly}
                  placeholder="Years"
                  step="1"
                  min="0"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className="form-input duration-input"
                />
              </div>
              {/* <div className="duration-input-group">
                <input
                  type="number"
                  name="expectedDurationMonth"
                  value={formData.expectedDurationMonth}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  placeholder="Months"
                  step="1"
                  className="form-input duration-input"
                />
              </div> */}
            </div>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col-12">
          <div className="form-group">
            <label className="form-label required">Objective of the Project</label>
            <div className="objective-checkbox-container">
              <div className="objective-checkbox-group">
                {projectObjectives.map(objective => (
                  <label key={objective} className="objective-checkbox-item">
                    <input
                      type="checkbox"
                      name="projectObjective"
                      value={objective}
                      checked={formData.projectObjective.includes(objective)}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          projectObjective: e.target.checked
                            ? [...prev.projectObjective, value]
                            : prev.projectObjective.filter(obj => obj !== value)
                        }));
                      }}
                      disabled={isReadOnly}
                      className="form-checkbox"
                    />
                    <span className="objective-checkbox-label">{objective}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col-12">
          <div className="form-group">
            <label className="form-label required">Project Description</label>
            <textarea
              name="projectDescription"
              value={formData.projectDescription}
              onChange={handleInputChange}
              required
              disabled={isReadOnly}
              placeholder="Describe your project in detail"
              rows={3}
              className="form-textarea"
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col-12">
          <div className="form-group">
            <label className="form-label">data+AI Capabilities Selection</label>
            {capabilitiesLoading ? (
              <ContentLoader compact label="Loading capabilities…" />
            ) : capabilities.length > 0 ? (
              <div className="capabilities-container-columns">
                <div className="checkbox-group-column">
                  {capabilities.slice(0, Math.ceil(capabilities.length / 2)).map(capability => (
                    <div key={capability.id} className="capability-group">
                      <button
                        type="button"
                        className="capability-main"
                        onClick={() => toggleVisibility(capability.id)}
                        aria-expanded={visibleSection === capability.id}
                      >
                        <span className="checkbox-label">{capability.capabilityName}</span>
                        <span className="expand-icon">▼</span>
                      </button>
                      {visibleSection === capability.id && capability.subCapabilities?.map(subCapability => {
                        const isSelected = formData.selectedCapabilities?.some(
                          (item) =>
                            item.capabilityId === capability.id &&
                            item.selectedSubCapabilities?.some(
                              sub => sub.subCapabilityId === subCapability.subId
                            )
                        ) || false;
                        
                        // Check if this sub-capability is approved or locked
                        const existingSubCapability = formData.selectedCapabilities
                          ?.find(item => item.capabilityId === capability.id)
                          ?.selectedSubCapabilities
                          ?.find(sub => sub.subCapabilityId === subCapability.subId);
                        
                        const isApproved = existingSubCapability?.state === "4";
                        const isLocked = existingSubCapability?.isNewLocked;
                        const isDisabled = isReadOnly || isApproved || isLocked || capabilitiesFromAPI;
                        
                        return (
                          <label key={subCapability.subId} className={`sub-capability ${isDisabled ? 'disabled' : ''}`}>
                            <input
                              type="checkbox"
                              name="dataAICapabilities"
                              value={`${capability.id}-${subCapability.subId}`}
                              checked={isSelected}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  e,
                                  capability.id,
                                  subCapability.subId
                                )
                              }
                              disabled={isDisabled}
                              className="form-checkbox"
                            />
                            <span className="checkbox-label">
                              {subCapability.subCapabilityName}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="checkbox-group-column">
                  {capabilities.slice(Math.ceil(capabilities.length / 2)).map(capability => (
                    <div key={capability.id} className="capability-group">
                      <button
                        type="button"
                        className="capability-main"
                        onClick={() => toggleVisibility(capability.id)}
                        aria-expanded={visibleSection === capability.id}
                      >
                        <span className="checkbox-label">{capability.capabilityName}</span>
                        <span className="expand-icon">▼</span>
                      </button>
                      {visibleSection === capability.id && capability.subCapabilities?.map(subCapability => {
                        const isSelected = formData.selectedCapabilities?.some(
                          (item) =>
                            item.capabilityId === capability.id &&
                            item.selectedSubCapabilities?.some(
                              sub => sub.subCapabilityId === subCapability.subId
                            )
                        ) || false;
                        
                        // Check if this sub-capability is approved or locked
                        const existingSubCapability = formData.selectedCapabilities
                          ?.find(item => item.capabilityId === capability.id)
                          ?.selectedSubCapabilities
                          ?.find(sub => sub.subCapabilityId === subCapability.subId);
                        
                        const isApproved = existingSubCapability?.state === "4";
                        const isLocked = existingSubCapability?.isNewLocked;
                        const isDisabled = isReadOnly || isApproved || isLocked || capabilitiesFromAPI;
                        
                        return (
                          <label key={subCapability.subId} className={`sub-capability ${isDisabled ? 'disabled' : ''}`}>
                            <input
                              type="checkbox"
                              name="dataAICapabilities"
                              value={`${capability.id}-${subCapability.subId}`}
                              checked={isSelected}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  e,
                                  capability.id,
                                  subCapability.subId
                                )
                              }
                              disabled={isDisabled}
                              className="form-checkbox"
                            />
                            <span className="checkbox-label">
                              {subCapability.subCapabilityName}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-options-text">No capabilities available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    return (
    <div className="step-content">
      <h5 className="step-title">
        <LineChart className="icon" />
        Business Value Drivers Selection
      </h5>
      
      <div className="form-row">
        <div className="form-col-12">
          <div className="form-group">
            <div className="form-label required">Select relevant business value drivers for your project:</div>
            <div className="objective-checkbox-container value-driver-checkbox-container">
              <div className="objective-checkbox-group">
                {businessValueDrivers.map(driver => (
                  <label key={driver} className="objective-checkbox-item">
                    <input
                      type="checkbox"
                      name="selectedValueDrivers"
                      value={driver}
                      checked={formData.selectedValueDrivers.includes(driver)}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          selectedValueDrivers: e.target.checked
                            ? [...prev.selectedValueDrivers, value]
                            : prev.selectedValueDrivers.filter(d => d !== value)
                        }));
                      }}
                      disabled={isReadOnly}
                      className="form-checkbox"
                    />
                    <span className="objective-checkbox-label">{driver}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderStep3 = () => {
    return (
    <div className="step-content">
      <h5 className="step-title">
        <DollarSign className="icon" />
        Estimated Project Costs
      </h5>
      
      <div className="form-row">
        <div className="form-col-6">
          <div className="form-group">
            <label className="form-label required">Expected Initial Implementation Cost ($)</label>
            <input
              type="text"
              name="initialImplementationCost"
              value={formattedValues.initialImplementationCost}
              onChange={(e) => handleFormattedNumberChange('initialImplementationCost', e.target.value)}
              required
              disabled={isReadOnly}
              placeholder="Enter initial implementation cost"
              className="form-input"
              maxLength={12}
            />
          </div>
        </div>
        <div className="form-col-6">
          <div className="form-group">
            <label className="form-label">Total Annual Recurring Costs ($)</label>
            <input
              type="text"
              name="annualRecurringCosts"
              value={formattedValues.annualRecurringCosts}
              readOnly
              placeholder="Auto-calculated from cost breakdown"
              className="form-input"
              style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
            />
          </div>
        </div>
      </div>

      <div className="cost-breakdown">
        <h6>Recurring Cost Breakdown:</h6>
        <div className="form-row">
          <div className="form-col-6">
            <div className="form-group">
              <label className="form-label">Annual Model Charges ($)</label>
              <input
                type="text"
                name="modelCharges"
                value={formattedValues.modelCharges}
                onChange={(e) => handleFormattedNumberChange('modelCharges', e.target.value)}
                disabled={isReadOnly}
                placeholder="Enter model charges"
                className="form-input"
                maxLength={12}
              />
            </div>
          </div>
          <div className="form-col-6">
            <div className="form-group">
              <label className="form-label">Annual License Fees ($)</label>
              <input
                type="text"
                name="licenseFees"
                value={formattedValues.licenseFees}
                onChange={(e) => handleFormattedNumberChange('licenseFees', e.target.value)}
                disabled={isReadOnly}
                placeholder="Enter license fees"
                className="form-input"
                maxLength={12}
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-col-6">
            <div className="form-group">
              <label className="form-label">Annual Maintenance Costs ($)</label>
              <input
                type="text"
                name="maintenanceCosts"
                value={formattedValues.maintenanceCosts}
                onChange={(e) => handleFormattedNumberChange('maintenanceCosts', e.target.value)}
                disabled={isReadOnly}
                placeholder="Enter maintenance costs"
                className="form-input"
                maxLength={12}
              />
            </div>
          </div>
          <div className="form-col-6">
            <div className="form-group">
              <label className="form-label">Annual Other Costs ($) (Please indicate below)</label>
              <input
                type="text"
                name="otherCosts"
                value={formattedValues.otherCosts}
                onChange={(e) => handleFormattedNumberChange('otherCosts', e.target.value)}
                disabled={isReadOnly}
                placeholder="Enter other costs"
                className="form-input"
                maxLength={12}
              />
            </div>
          </div>
        </div>

        {formData.otherCosts > 0 && (
          <div className="form-row">
            <div className="form-col-12">
              <div className="form-group">
                <label className="form-label">Other Costs Description</label>
                <input
                  type="text"
                  name="otherCostsDescription"
                  value={formData.otherCostsDescription}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  placeholder="Specify what other costs include"
                  className="form-input"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderStep4 = () => {
    return (
    <div className="step-content">
      <h5 className="step-title">
        <BarChart className="icon" />
        Value Driver Responses
      </h5>
      <p className="step-description">
        For each selected value driver, provide the baseline annual cost and expected AI improvement percentage. 
        The derived savings will be calculated automatically.
      </p>
      
      {formData.selectedValueDrivers.includes('Revenue Growth') && (
        <div className="value-driver-section">
          <h6 className="value-driver-title">Revenue Growth</h6>
          <div className="form-row">
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label required">Baseline Annual Revenue ($)</label>
                <input
                  type="text"
                  name="revenueGrowthBaselineCost"
                  value={formattedValues.revenueGrowthBaselineCost}
                  onChange={(e) => handleFormattedNumberChange('revenueGrowthBaselineCost', e.target.value)}
                  placeholder="Enter baseline annual revenue"
                  disabled={isReadOnly}
                  className="form-input"
                  maxLength={12}
                />
              </div>
            </div>
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label required">AI Improvement (%)</label>
                <PercentageInput
                  name="revenueGrowthAIImprovement"
                  value={formData.revenueGrowthAIImprovement}
                  onChange={handleInputChange}
                  placeholder="Enter AI improvement percentage"
                  disabled={isReadOnly}
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>
            </div>
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label">Derived Annual Revenue ($)</label>
                <input
                  type="text"
                  name="revenueGrowthDerivedSavings"
                  value={formattedValues.revenueGrowthDerivedSavings}
                  readOnly
                  className="form-input"
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {formData.selectedValueDrivers.includes('Cost Reduction') && (
        <div className="value-driver-section">
          <h6 className="value-driver-title">Cost Reduction</h6>
          <div className="form-row">
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label required">Baseline Annual Cost ($)</label>
                <input
                  type="text"
                  name="costReductionBaselineCost"
                  value={formattedValues.costReductionBaselineCost}
                  onChange={(e) => handleFormattedNumberChange('costReductionBaselineCost', e.target.value)}
                  placeholder="Enter baseline annual Cost"
                  disabled={isReadOnly}
                  className="form-input"
                  maxLength={12}
                />
              </div>
            </div>
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label required">AI Improvement (%)</label>
                <PercentageInput
                  name="costReductionAIImprovement"
                  value={formData.costReductionAIImprovement}
                  onChange={handleInputChange}
                  placeholder="Enter AI improvement percentage"
                  disabled={isReadOnly}
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>
            </div>
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label">Derived Annual Saving ($)</label>
                <input
                  type="text"
                  name="costReductionDerivedSavings"
                  value={formattedValues.costReductionDerivedSavings}
                  readOnly
                  className="form-input"
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {formData.selectedValueDrivers.includes('Operational Efficiency') && (
        <div className="value-driver-section">
          <h6 className="value-driver-title">Operational Efficiency</h6>
          <div className="form-row">
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label required">Baseline Annual Cost ($)</label>
                <input
                  type="text"
                  name="operationalEfficiencyBaselineCost"
                  value={formattedValues.operationalEfficiencyBaselineCost}
                  onChange={(e) => handleFormattedNumberChange('operationalEfficiencyBaselineCost', e.target.value)}
                  placeholder="Enter baseline annual Cost"
                  disabled={isReadOnly}
                  className="form-input"
                  maxLength={12}
                />
              </div>
            </div>
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label required">AI Improvement (%)</label>
                <PercentageInput
                  name="operationalEfficiencyAIImprovement"
                  value={formData.operationalEfficiencyAIImprovement}
                  onChange={handleInputChange}
                  placeholder="Enter AI improvement percentage"
                  disabled={isReadOnly}
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>
            </div>
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label">Derived Annual Saving ($)</label>
                <input
                  type="text"
                  name="operationalEfficiencyDerivedSavings"
                  value={formattedValues.operationalEfficiencyDerivedSavings}
                  readOnly
                  className="form-input"
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {formData.selectedValueDrivers.includes('Risk & Compliance') && (
        <div className="value-driver-section">
          <h6 className="value-driver-title">Risk & Compliance</h6>
          <div className="form-row">
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label required">Baseline Annual Cost ($)</label>
                <input
                  type="text"
                  name="riskComplianceBaselineCost"
                  value={formattedValues.riskComplianceBaselineCost}
                  onChange={(e) => handleFormattedNumberChange('riskComplianceBaselineCost', e.target.value)}
                  placeholder="Enter baseline annual Cost"
                  disabled={isReadOnly}
                  className="form-input"
                  maxLength={12}
                />
              </div>
            </div>
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label required">AI Improvement (%)</label>
                <PercentageInput
                  name="riskComplianceAIImprovement"
                  value={formData.riskComplianceAIImprovement}
                  onChange={handleInputChange}
                  placeholder="Enter AI improvement percentage"
                  disabled={isReadOnly}
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>
            </div>
            <div className="form-col-4">
              <div className="form-group">
                <label className="form-label">Derived Annual Saving ($)</label>
                <input
                  type="text"
                  name="riskComplianceDerivedSavings"
                  value={formattedValues.riskComplianceDerivedSavings}
                  readOnly
                  className="form-input"
                  style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderStep5 = () => {
    return (
    <div className="step-content">
      <h5 className="step-title">
        <Calculator className="icon" />
        Cost of Capital, Effort, and Adoption Rate
      </h5>
      
      <div className="form-row">
        <div className="form-col-4">
          <div className="form-group">
            <label className="form-label required">Cost of Capital/Discount Rate (WACC%)</label>
            <PercentageInput
              name="discountRate"
              value={formData.discountRate}
              onChange={handleInputChange}
              disabled={isReadOnly}
              placeholder="Enter percentage"
              min={0}
              max={100}
              step={0.1}
            />
          </div>
        </div>
        {/* <div className="form-col-4">
          <div className="form-group">
            <label className="form-label required">Project Implementation Effort (1-5)</label>
            <select
              name="implementationEffort"
              value={formData.implementationEffort === 1 ? "" : formData.implementationEffort}
              onChange={handleInputChange}
              required
              disabled={isReadOnly}
              className="form-select"
            >
              <option value="">Select effort level</option>
              <option value={1}>1 - Very Low</option>
              <option value={2}>2 - Low</option>
              <option value={3}>3 - Medium</option>
              <option value={4}>4 - High</option>
              <option value={5}>5 - Very High</option>
            </select>
          </div>
        </div> */}
        <div className="form-col-4">
          <div className="form-group">
            <label className="form-label">Shared Impact (Optional)</label>
            <PercentageInput
              name="sharedImpact"
              value={formData.sharedImpact}
              onChange={handleInputChange}
              disabled={isReadOnly}
              placeholder="Enter shared impact percentage"
              min={0}
              max={100}
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col-12">
          <div className="form-group">
            <label className="form-label required">Expected Adoption Rate Over Time (%)</label>
            <div className="adoption-rate-container">
              {formData.adoptionRateOverTime.map((rate, index) => {
                return (
                  <PercentageInput
                    key={index}
                    name={`adoptionRate_${index}`}
                    value={rate}
                    onChange={(e) => handleAdoptionRateChange(index, e.target.value)}
                    disabled={isReadOnly}
                    placeholder={`Enter adoption rate (year ${index + 1}) %`}
                    min={0}
                    max={100}
                    step={0.1}
                    className="form-input adoption-rate-input"
                  />
                );
              })}
              {formData.adoptionRateOverTime.length === 0 && (
                <div className="no-periods-message">
                  Please enter the project duration above to generate adoption rate periods.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
    );
  };

  // Helper function to get available options for second dropdown
  const getAvailableSecondOptions = (firstSelection) => {
    const allOptions = [
      { value: 'wacc', label: 'WACC%' },
      { value: 'annualSaving', label: 'Annual Saving ($)' },
      { value: 'expectedInitialImplementationCost', label: 'Expected Initial Implementation Cost ($)' },
      { value: 'totalAnnualRecurringCosts', label: 'Total Annual Recurring Costs ($)' }
    ];
    return allOptions.filter(option => option.value !== firstSelection);
  };

  // Helper function to get field value from previous inputs
  const getFieldValue = (fieldType) => {
    switch (fieldType) {
      case 'wacc':
        return formData.discountRate || 0;
      case 'annualSaving':
        const totalSavings = (formData.selectedValueDrivers.includes('Revenue Growth') ? (formData.revenueGrowthDerivedSavings || 0) : 0) +
          (formData.selectedValueDrivers.includes('Cost Reduction') ? (formData.costReductionDerivedSavings || 0) : 0) +
          (formData.selectedValueDrivers.includes('Operational Efficiency') ? (formData.operationalEfficiencyDerivedSavings || 0) : 0) +
          (formData.selectedValueDrivers.includes('Risk & Compliance') ? (formData.riskComplianceDerivedSavings || 0) : 0);
        return totalSavings;
      case 'expectedInitialImplementationCost':
        return formData.initialImplementationCost || 0;
      case 'totalAnnualRecurringCosts':
        return formData.annualRecurringCosts || 0;
      default:
        return 0;
    }
  };

  // Helper function to format field value for display
  const formatFieldValue = (fieldType, value) => {
    switch (fieldType) {
      case 'wacc':
        return `${value}%`;
      case 'annualSaving':
      case 'expectedInitialImplementationCost':
      case 'totalAnnualRecurringCosts':
        return formatCurrency(value);
      default:
        return value;
    }
  };

  // Helper function to calculate low and high values based on base value and variation amount
  const calculateSensitivityValues = (fieldType, baseValue, variationAmount) => {
    const variation = parseFloat(variationAmount) || 0;
    
    // For WACC (percentage), variation is in percentage points
    if (fieldType === 'wacc') {
      const lowValue = Math.max(0, baseValue - variation); // Ensure minimum 0%
      const highValue = Math.min(100, baseValue + variation); // Ensure maximum 100%
      return { lowValue, highValue };
    }
    
    // For other fields (currency), variation is now treated as percentage of base value
    const variationPercent = variation / 100; // Convert percentage to decimal
    const lowValue = Math.max(0, baseValue * (1 - variationPercent)); // Apply percentage decrease
    const highValue = baseValue * (1 + variationPercent); // Apply percentage increase
    return { lowValue, highValue };
  };

  // Calculate sensitivity analysis scenarios
  const calculateSensitivityScenarios = () => {
    if (!formData.roiSensitivityType || !formData.secondSensitivityField || 
        !formData.firstParamValue || !formData.secondParamValue ||
        !formData.roiSensitivityRate || !formData.secondSensitivityRate) {
      return { tableData: [] };
    }

    const firstFieldValue = getFieldValue(formData.roiSensitivityType);
    const secondFieldValue = getFieldValue(formData.secondSensitivityField);

    // Calculate low and high values for first field based on previous value and variation amount
    const firstVariationAmount = parseFloat(formData.firstParamValue) || 0;
    const firstSensitivityValues = calculateSensitivityValues(formData.roiSensitivityType, firstFieldValue, firstVariationAmount);
    const firstFieldScenarios = [
      firstSensitivityValues.lowValue, // Low (previous value - variation)
      firstFieldValue, // Base (previous value)
      firstSensitivityValues.highValue  // High (previous value + variation)
    ];

    // Calculate low and high values for second field based on previous value and variation amount
    const secondVariationAmount = parseFloat(formData.secondParamValue) || 0;
    const secondSensitivityValues = calculateSensitivityValues(formData.secondSensitivityField, secondFieldValue, secondVariationAmount);
    const secondFieldScenarios = [
      secondSensitivityValues.lowValue, // Low (previous value - variation)
      secondFieldValue, // Base (previous value)
      secondSensitivityValues.highValue  // High (previous value + variation)
    ];

    // Create table data (3x3 matrix)
    const tableData = [];
    for (let i = 0; i < 3; i++) {
      const row = [];
      for (let j = 0; j < 3; j++) {
        // Calculate ROI for this scenario (simplified calculation)
        const implementationCost = formData.roiSensitivityType === 'expectedInitialImplementationCost' ? firstFieldScenarios[i] : 
                                 formData.secondSensitivityField === 'expectedInitialImplementationCost' ? secondFieldScenarios[j] : 
                                 parseFloat(formData.initialImplementationCost) || 0;
        
        const annualSaving = formData.roiSensitivityType === 'annualSaving' ? firstFieldScenarios[i] : 
                           formData.secondSensitivityField === 'annualSaving' ? secondFieldScenarios[j] : 
                           getFieldValue('annualSaving');
        
        const wacc = formData.roiSensitivityType === 'wacc' ? firstFieldScenarios[i] : 
                    formData.secondSensitivityField === 'wacc' ? secondFieldScenarios[j] : 
                    parseFloat(formData.discountRate) || 0;
        
        const annualRecurringCosts = formData.roiSensitivityType === 'totalAnnualRecurringCosts' ? firstFieldScenarios[i] : 
                                   formData.secondSensitivityField === 'totalAnnualRecurringCosts' ? secondFieldScenarios[j] : 
                                   parseFloat(formData.annualRecurringCosts) || 0;

        // Simple ROI calculation: (Annual Savings - Annual Recurring Costs) / Implementation Cost * 100
        const netAnnualBenefit = annualSaving - annualRecurringCosts;
        const roi = implementationCost > 0 ? (netAnnualBenefit / implementationCost) * 100 : 0;
        
        row.push({
          roi: roi.toFixed(1),
          npv: (netAnnualBenefit * 5 - implementationCost).toFixed(0) // Simplified 5-year NPV
        });
      }
      tableData.push(row);
    }

    return {
      tableData
    };
  };


  const renderStep6 = () => {
    const sensitivityData = calculateSensitivityScenarios();
    const availableSecondOptions = getAvailableSecondOptions(formData.roiSensitivityType);

    // Debug: Log the current form data state
    console.log('🔍 renderStep6 - Current form data:', {
      roiSensitivityType: formData.roiSensitivityType,
      roiSensitivityRate: formData.roiSensitivityRate,
      firstParamValue: formData.firstParamValue,
      secondSensitivityField: formData.secondSensitivityField,
      secondParamValue: formData.secondParamValue,
      secondSensitivityRate: formData.secondSensitivityRate,
      availableSecondOptions: availableSecondOptions,
      sensitivityDataTableLength: sensitivityData.tableData.length
    });

    return (
    <div className="step-content">
      <h5 className="step-title">
        <LineChart className="icon" />
        ROI Sensitivity Analysis
      </h5>
      <p className="step-description">
          Analyze how sensitive your ROI is to changes in key parameters. Select two parameters and set custom high/low values for each.
      </p>

      <div className="form-section">
          {/* First Field Selection */}
        <div className="form-row">
          <div className="form-col-12">
            <div className="form-group">
                <label className="form-label required">First Parameter</label>
              <select
                name="roiSensitivityType"
                value={formData.roiSensitivityType}
                  onChange={(e) => {
                    handleInputChange(e);
                    // Reset second field and custom values when first field changes
                    setFormData(prev => ({
                      ...prev,
                      secondSensitivityField: "",
                      firstParamLowValue: "",
                      firstParamHighValue: "",
                      secondParamLowValue: "",
                      secondParamHighValue: ""
                    }));
                  }}
                disabled={isReadOnly}
                className="form-select"
              >
                  <option value="">Select first parameter</option>
                  <option value="wacc">WACC%</option>
                  <option value="annualSaving">Annual Saving ($)</option>
                  <option value="expectedInitialImplementationCost">Expected Initial Implementation Cost ($)</option>
                  <option value="totalAnnualRecurringCosts">Total Annual Recurring Costs ($)</option>
              </select>
            </div>
          </div>
        </div>

          {/* First Field Value and Sensitivity Rate */}
          {formData.roiSensitivityType && (
          <div className="form-row">
              <div className="form-col-4">
              <div className="form-group">
                  <label className="form-label">Current Value</label>
                <input
                  type="text"
                    value={formatFieldValue(formData.roiSensitivityType, getFieldValue(formData.roiSensitivityType))}
                  readOnly
                  className="form-input readonly"
                />
              </div>
            </div>
              <div className="form-col-4">
                <div className="form-group">
                  <label className="form-label required">
                    Variation Amount (%)
                  </label>
                  <PercentageInput
                    name="firstParamValue"
                    value={formData.firstParamValue}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    placeholder="Enter percentage"
                    min={0}
                    max={formData.roiSensitivityType === 'wacc' ? 100 - getFieldValue(formData.roiSensitivityType) : 100}
                    step={0.01}
                  />
                </div>
              </div>
              <div className="form-col-4">
                <div className="form-group">
                  <label className="form-label required">Sensitivity Rate (%)</label>
                <PercentageInput
                  name="roiSensitivityRate"
                  value={formData.roiSensitivityRate}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  placeholder="Enter percentage"
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>
            </div>
          </div>
        )}


          {/* Second Field Selection */}
          {formData.roiSensitivityType && formData.roiSensitivityType !== "" && 
           formData.firstParamValue && formData.firstParamValue !== "" && 
           formData.roiSensitivityRate && formData.roiSensitivityRate !== "" && (
          <div className="form-row">
              <div className="form-col-12">
              <div className="form-group">
                  <label className="form-label required">Second Parameter</label>
                  <select
                    name="secondSensitivityField"
                    value={formData.secondSensitivityField}
                    onChange={(e) => {
                      handleInputChange(e);
                      // Reset second field custom values when second field changes
                      setFormData(prev => ({
                        ...prev,
                        secondParamValue: ""
                      }));
                    }}
                    disabled={isReadOnly}
                    className="form-select"
                  >
                    <option value="">Select second parameter</option>
                    {availableSecondOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                    </div>
                    </div>
                    </div>
                  )}

          {/* Second Field Value and Sensitivity Rate */}
          {formData.secondSensitivityField && formData.secondSensitivityField !== "" && (
            <div className="form-row">
              <div className="form-col-4">
                <div className="form-group">
                  <label className="form-label">Current Value</label>
                  <input
                    type="text"
                    value={formatFieldValue(formData.secondSensitivityField, getFieldValue(formData.secondSensitivityField))}
                    readOnly
                    className="form-input readonly"
                  />
                    </div>
                  </div>
              <div className="form-col-4">
                <div className="form-group">
                  <label className="form-label required">
                    Variation Amount (%)
                  </label>
                  <PercentageInput
                    name="secondParamValue"
                    value={formData.secondParamValue}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    placeholder="Enter percentage"
                    min={0}
                    max={formData.secondSensitivityField === 'wacc' ? 100 - getFieldValue(formData.secondSensitivityField) : 100}
                    step={0.01}
                  />
                </div>
              </div>
              <div className="form-col-4">
              <div className="form-group">
                  <label className="form-label required">Sensitivity Rate (%)</label>
                <PercentageInput
                  name="secondSensitivityRate"
                  value={formData.secondSensitivityRate}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  placeholder="Enter percentage"
                  min={0}
                  max={100}
                  step={0.1}
                />
              </div>
            </div>
          </div>
        )}


          {/* Sensitivity Analysis Table */}
          {formData.roiSensitivityType && formData.roiSensitivityType !== "" &&
           formData.secondSensitivityField && formData.secondSensitivityField !== "" && 
           formData.firstParamValue && formData.firstParamValue !== "" &&
           formData.secondParamValue && formData.secondParamValue !== "" &&
           formData.roiSensitivityRate && formData.roiSensitivityRate !== "" &&
           formData.secondSensitivityRate && formData.secondSensitivityRate !== "" && (
            <div className="form-row">
              <div className="form-col-12">
                <div className="sensitivity-analysis-results">
                  <h6>Sensitivity Analysis Results</h6>
                  
                  {/* Table */}
                  <div className="sensitivity-table-container" style={{ marginBottom: '20px', overflowX: 'auto' }}>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th rowSpan="2">{formData.secondSensitivityField === 'wacc' ? 'WACC' : 
                               formData.secondSensitivityField === 'annualSaving' ? 'Annual Saving' :
                               formData.secondSensitivityField === 'expectedInitialImplementationCost' ? 'Implementation Cost' :
                               'Annual Recurring Costs'}</th>
                          <th colSpan="3" style={{ textAlign: 'center' }}>
                            {formData.roiSensitivityType === 'wacc' ? 'WACC' : 
                             formData.roiSensitivityType === 'annualSaving' ? 'Annual Saving' :
                             formData.roiSensitivityType === 'expectedInitialImplementationCost' ? 'Implementation Cost' :
                             'Annual Recurring Costs'}
                          </th>
                        </tr>
                        <tr>
                          <th>{formatFieldValue(formData.roiSensitivityType, 
                              calculateSensitivityValues(formData.roiSensitivityType, getFieldValue(formData.roiSensitivityType), parseFloat(formData.firstParamValue)).lowValue)}</th>
                          <th>{formatFieldValue(formData.roiSensitivityType, getFieldValue(formData.roiSensitivityType))}</th>
                          <th>{formatFieldValue(formData.roiSensitivityType, 
                              calculateSensitivityValues(formData.roiSensitivityType, getFieldValue(formData.roiSensitivityType), parseFloat(formData.firstParamValue)).highValue)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sensitivityData.tableData.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            <td>
                              {rowIndex === 0 ? formatFieldValue(formData.secondSensitivityField, 
                                  calculateSensitivityValues(formData.secondSensitivityField, getFieldValue(formData.secondSensitivityField), parseFloat(formData.secondParamValue)).lowValue) :
                               rowIndex === 1 ? formatFieldValue(formData.secondSensitivityField, getFieldValue(formData.secondSensitivityField)) :
                               formatFieldValue(formData.secondSensitivityField, 
                                  calculateSensitivityValues(formData.secondSensitivityField, getFieldValue(formData.secondSensitivityField), parseFloat(formData.secondParamValue)).highValue)}
                            </td>
                            {row.map((cell, colIndex) => (
                              <td key={colIndex}>
                                <div>ROI: {cell.roi}%</div>
                                <div>NPV: {formatCurrency(cell.npv)}</div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                </div>
              </div>
            </div>
          )}
      </div>
    </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      default:
        return renderStep1();
    }
  };

  // Enhanced PDF Download function

  // Calculate project metrics
  const calculateProjectMetrics = useCallback(() => {
    const implementationCost = parseFloat(formData.initialImplementationCost) || 0;
    const annualSavings = (formData.revenueGrowthDerivedSavings || 0) + 
                         (formData.costReductionDerivedSavings || 0) + 
                         (formData.operationalEfficiencyDerivedSavings || 0) + 
                         (formData.riskComplianceDerivedSavings || 0);
    const annualRecurringCosts = parseFloat(formData.annualRecurringCosts) || 0;
    const projectTerm = Math.round((parseInt(formData.expectedDurationYear) || 0) + ((parseInt(formData.expectedDurationMonth) || 0) / 12));
    const discountRate = parseFloat(formData.discountRate) || 0;
    const sharedImpact = parseFloat(formData.sharedImpact) || 0;
    
    // Calculate NPV, IRR, Payback, ROI (simplified calculations)
    const netAnnualCashFlow = annualSavings - annualRecurringCosts;
    const adjustedCashFlow = netAnnualCashFlow * (1 - sharedImpact);
    
    // Simple NPV calculation
    let npv = -implementationCost;
    for (let year = 1; year <= projectTerm; year++) {
      const adoptionRate = formData.adoptionRateOverTime[year - 1] ? 
        parseFloat(formData.adoptionRateOverTime[year - 1]) / 100 : 1;
      const yearlyCashFlow = adjustedCashFlow * adoptionRate;
      npv += yearlyCashFlow / Math.pow(1 + discountRate / 100, year);
    }
    
    // Simple ROI calculation
    const totalSavings = adjustedCashFlow * projectTerm;
    const roi = ((totalSavings - implementationCost) / implementationCost) * 100;
    
    // Simple payback calculation
    let payback = 0;
    let cumulativeCashFlow = -implementationCost;
    for (let year = 1; year <= projectTerm; year++) {
      const adoptionRate = formData.adoptionRateOverTime[year - 1] ? 
        parseFloat(formData.adoptionRateOverTime[year - 1]) / 100 : 1;
      const yearlyCashFlow = adjustedCashFlow * adoptionRate;
      cumulativeCashFlow += yearlyCashFlow;
      if (cumulativeCashFlow >= 0 && payback === 0) {
        payback = year - 1 + (Math.abs(cumulativeCashFlow - yearlyCashFlow) / yearlyCashFlow);
        break;
      }
    }
    
    return {
      implementationCost,
      annualSavings,
      annualRecurringCosts,
      projectTerm,
      discountRate,
      sharedImpact,
      npv: Math.round(npv),
      roi: Math.round(roi),
      payback: payback.toFixed(1),
      irr: Math.round(roi * 0.8) // Simplified IRR calculation
    };
  }, [formData]);

  const renderSummaryView = () => {
    // Use API data if available, otherwise fall back to calculated metrics
    const useApiData = projectSummaryData && projectSummaryData.cashFlowCalculationResult;
    
    let metrics, implementationCost, annualSavings, annualRecurringCosts, projectTerm, discountRate, sharedImpact;
    let npv, irr, payback, roi;
    
    if (useApiData) {
      // Use data from API response
      const apiData = projectSummaryData;
      const cashFlowResult = apiData.cashFlowCalculationResult;
      
      implementationCost = apiData.initialImplementationCost || 0;
      annualRecurringCosts = apiData.annualRecurringCosts || 0;
      projectTerm = apiData.projectTermValue || apiData.expectedDurationYears || 0;
      discountRate = apiData.discountRate || 0;
      sharedImpact = apiData.sharedImpact || 0;
      
      // Key financial metrics from API
      npv = cashFlowResult.netPresentValue || 0;
      irr = cashFlowResult.internalRateOfReturn || 0;
      payback = cashFlowResult.paybackPeriodMonths || 0;
      roi = cashFlowResult.roiYear1Percentage || 0;
      
      // Debug the actual values being used
      console.log('🔍 API Financial Metrics:', {
        npv: npv,
        irr: irr,
        payback: payback,
        roi: roi,
        rawNPV: cashFlowResult.netPresentValue,
        rawIRR: cashFlowResult.internalRateOfReturn,
        rawPayback: cashFlowResult.paybackPeriodMonths,
        rawROI: cashFlowResult.roiYear1Percentage
      });
      
      // Calculate annual savings from value driver responses
      annualSavings = apiData.valueDriverResponses?.reduce((total, driver) => total + (driver.derivedSavings || 0), 0) || 0;
      
      metrics = {
        implementationCost,
        annualSavings,
        annualRecurringCosts,
        projectTerm,
        discountRate,
        sharedImpact,
        npv,
        irr,
        payback,
        roi
      };
    } else {
      // Fall back to calculated metrics
      console.log('🔍 Using calculated metrics (fallback)');
      metrics = calculateProjectMetrics();
      implementationCost = metrics.implementationCost;
      annualSavings = metrics.annualSavings;
      annualRecurringCosts = metrics.annualRecurringCosts;
      projectTerm = metrics.projectTerm;
      discountRate = metrics.discountRate;
      sharedImpact = metrics.sharedImpact;
      npv = metrics.npv;
      irr = metrics.irr;
      payback = metrics.payback;
      roi = metrics.roi;
    }
    
    // Calculate adoption impact rate string
    const getAdoptionImpactRate = () => {
      if (useApiData && projectSummaryData.adoptionRates) {
        return projectSummaryData.adoptionRates.map(rate => 
          `Y${rate.periodNumber}: ${rate.adoptionPercentage}%`
        ).join('; ');
      } else {
        const years = parseInt(formData.expectedDurationYear) || 0;
        return formData.adoptionRateOverTime.slice(0, years).map((rate, index) => 
          `Y${index + 1}: ${rate && rate !== "" ? rate : "Not set"}%`
        ).join('; ');
      }
    };

    // Get implementation effort text
    const getImplementationEffortText = (effort) => {
      const effortMap = {
        1: 'Very Low',
        2: 'Low', 
        3: 'Medium',
        4: 'High',
        5: 'Very High'
      };
      return `${effort} (${effortMap[effort] || 'Unknown'})`;
    };

    // Format currency
    const formatCurrency = (value) => {
      const num = parseFloat(value);
      if (isNaN(num)) return '$0';
      
      // Round to avoid floating point precision errors
      const roundedNum = Math.round(num * 1000000) / 1000000;
      
      // Check if the number has meaningful decimal places after rounding
      const hasDecimals = Math.abs(roundedNum % 1) > 0.000001;
      
      if (hasDecimals) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 1
        }).format(roundedNum);
      } else {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(Math.round(roundedNum));
      }
    };

    return (
      <div className="summary-view-container">
        <div className="summary-header">
          <h2 className="summary-title">Project Summary</h2>
        </div>

        <div className="summary-content">
          {/* Project Parameters */}
          <div className="summary-section">
            <div className="section-label">Project Parameters:</div>
            <div className="parameters-list">
              <div className="parameter-item">
                <span className="parameter-label">Project term:</span>
                <span className="parameter-value">{projectTerm} years</span>
              </div>
              <div className="parameter-item">
                <span className="parameter-label">Implementation cost =</span>
                <span className="parameter-value">{formatCurrency(implementationCost)}</span>
              </div>
              <div className="parameter-item">
                <span className="parameter-label">Annual financial saving =</span>
                <span className="parameter-value">{formatCurrency(annualSavings)}</span>
              </div>
              <div className="parameter-item">
                <span className="parameter-label">Annual recurring costs =</span>
                <span className="parameter-value">{formatCurrency(annualRecurringCosts)}</span>
              </div>
              <div className="parameter-item">
                <span className="parameter-label">Adoption impact rate =</span>
                <span className="parameter-value">{getAdoptionImpactRate()}</span>
              </div>
              <div className="parameter-item">
                <span className="parameter-label">Implementation effort =</span>
                <span className="parameter-value">{useApiData ? getImplementationEffortText(projectSummaryData.implementationEffortRating) : getImplementationEffortText(formData.implementationEffort)}</span>
              </div>
              <div className="parameter-item">
                <span className="parameter-label">Shared impact =</span>
                <span className="parameter-value">{sharedImpact}</span>
              </div>
              <div className="parameter-item">
                <span className="parameter-label">Project discount rate (WACC%) =</span>
                <span className="parameter-value">{discountRate}%</span>
              </div>
            </div>
          </div>

          {/* Key Financial Metrics Summary */}
          <div className="summary-section">
            <div className="section-label">Key Financial Metrics Summary:</div>
            <div className="financial-metrics-grid">
              <div className="metric-item">
                <span className="metric-value">{formatCurrency(npv)}</span>
                <span className="metric-label">NPV</span>
              </div>
              <div className="metric-item">
                <span className="metric-value">{formatPercentage(irr)}</span>
                <span className="metric-label">IRR</span>
              </div>
              <div className="metric-item">
                <span className="metric-value">{useApiData ? `${(payback / 12).toFixed(1)} Years` : `${payback} Years`}</span>
                <span className="metric-label">Payback</span>
              </div>
              <div className="metric-item">
                <span className="metric-value">{formatPercentage(roi)}</span>
                <span className="metric-label">ROI</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="summary-actions">
          <button
            type="button"
            onClick={() => setShowSummary(false)}
            className="action-button secondary"
          >
            <ArrowLeft />
            Back to Form
          </button>
          <button
            type="button"
            onClick={handleFinalSubmit}
            disabled={loading}
            className="action-button primary"
          >
            {loading && <span className="spinner"></span>}
            <Save />
            {loading ? 'Submitting...' : 'Generate Report'}
          </button>
        </div>
      </div>
    );
  };


  // Render projects list view
  const renderProjectsListView = () => {
    // Helper function to get status badge class
    const getStatusBadgeClass = (status) => {
      switch (status?.toLowerCase()) {
        case 'complete':
        case 'completed':
          return 'bg-success';
        case 'draft':
          return 'bg-warning';
        case 'saved':
          return 'bg-info';
        default:
          return 'bg-secondary';
      }
    };

    const handleEditProject = (project) => {
      // Set the project ID and switch to form view
      setSelectedProjectId(project.projectId);
      setBusinessValueProjectId(project.id || 0);
      setShowListView(false);
      setShowReport(false);
      // Skip project type selection for existing projects
      setShowProjectTypeSelection(false);
      setProjectType('existing');
      setSelectedExistingProject(project);
      // Reset the new report flag since this is editing an existing project
      setIsNewReportForExistingProject(false);
      setCapabilitiesFromAPI(false);
      // Set read-only state based on project status
      const projectStatus = project.saveStage || 'Draft';
      setCurrentProjectStatus(projectStatus);
      setIsReadOnly(projectStatus === 'Complete');
      // The useEffect will automatically call loadSavedBusinessValueData when businessValueProjectId changes
    };

    const handleViewProject = (project) => {
      // Set the project ID and load data for viewing
      setSelectedProjectId(project.projectId);
      setBusinessValueProjectId(project.id || 0);
      setShowListView(false);
      setShowReport(false);
      // Skip project type selection for existing projects
      setShowProjectTypeSelection(false);
      setProjectType('existing');
      setSelectedExistingProject(project);
      // Reset the new report flag since this is viewing an existing project
      setIsNewReportForExistingProject(false);
      // Set read-only state based on project status immediately
      const projectStatus = project.saveStage || 'Draft';
      setCurrentProjectStatus(projectStatus);
      const isReadOnlyMode = projectStatus === 'Complete';
      setIsReadOnly(isReadOnlyMode);
      // Always start at step 1 in view mode
      setCurrentStep(1);
      console.log('View project - Status:', projectStatus, 'Read-only mode:', isReadOnlyMode);
      
      // The useEffect will automatically call loadSavedBusinessValueData when businessValueProjectId changes
    };

    const handleViewReport = async (project) => {
      try {
        setGeneratingReport(true);
        setSelectedProjectId(project.projectId);
        setBusinessValueProjectId(project.id || 0);
        // Skip project type selection for existing projects
        setShowProjectTypeSelection(false);
        setProjectType('existing');
        setSelectedExistingProject(project);
        // Reset the new report flag since this is working with an existing project
        setIsNewReportForExistingProject(false);
        // Set read-only state based on project status
        const projectStatus = project.saveStage || 'Draft';
        setCurrentProjectStatus(projectStatus);
        setIsReadOnly(projectStatus === 'Complete');
        
        // Load the project data to get the existing cashFlowCalculationResult
        console.log('🔄 Loading project data for report viewing...');
        const projectData = await loadProjectDataForReport(project.id || 0, capabilities, projectObjectives);
        
        if (!projectData) {
          toast.error("Failed to load project data for report viewing");
          return;
        }
        
        console.log('✅ Project data loaded, displaying existing report...');
        
        // Use the existing cashFlowCalculationResult from the project data
        if (projectData.cashFlowCalculationResult) {
          setCashFlowReport(projectData.cashFlowCalculationResult);
          setShowReport(true);
          setShowListView(false);
          toast.success("Report loaded successfully!");
        } else {
          toast.error("No existing report found for this project");
        }
      } catch (error) {
        console.error("Error loading report:", error);
        toast.error("Failed to load report");
      } finally {
        setGeneratingReport(false);
      }
    };

    const handleGenerateReport = async (project) => {
      try {
        setGeneratingReport(true);
        setSelectedProjectId(project.projectId);
        setBusinessValueProjectId(project.id || 0);
        // Skip project type selection for existing projects
        setShowProjectTypeSelection(false);
        setProjectType('existing');
        setSelectedExistingProject(project);
        // Reset the new report flag since this is working with an existing project
        setIsNewReportForExistingProject(false);
        // Set read-only state based on project status
        const projectStatus = project.saveStage || 'Draft';
        setCurrentProjectStatus(projectStatus);
        setIsReadOnly(projectStatus === 'Complete');
        
        // First, load the project data synchronously
        console.log('🔄 Loading project data for report generation...');
        const projectData = await loadProjectDataForReport(project.id || 0, capabilities, projectObjectives);
        
        if (!projectData) {
          toast.error("Failed to load project data for report generation");
          return;
        }
        
        console.log('✅ Project data loaded, generating new cash flow report...');
        
        // Generate report using the new utility function
        const reportResult = await generateReport(projectData, formData, capabilities, projectObjectives);
        
        if (reportResult.success) {
          setCashFlowReport(reportResult.data);
          setShowReport(true);
          setShowListView(false);
          toast.success("Report generated successfully!");
        } else {
          toast.error(reportResult.error || "Failed to generate report");
        }
      } catch (error) {
        console.error("Error generating report:", error);
        toast.error("Failed to generate report");
      } finally {
        setGeneratingReport(false);
      }
    };

    return (
      <div className="projects-list-container">
        <div className="projects-list-header">
          <button
            type="button"
            className="btn-dashboard-primary mb-3"
            onClick={() => {
              setShowListView(false);
              resetForm();
              setShowProjectTypeSelection(true);
            }}
          >
            <Plus className="me-2" />
            Evaluate your project
          </button>
        </div>

        {projectsLoading ? (
          <ContentLoader label="Loading projects…" />
        ) : projectsError ? (
          <div className="alert alert-danger" role="alert">
            {projectsError}
          </div>
        ) : projectsList.length === 0 ? (
          <div className="text-center py-5">
            <FileText className="h-12 w-12 text-muted mb-3" />
            <h5 className="text-muted">No projects found</h5>
            <p className="text-muted">Create your first value estimation project to get started.</p>
          </div>
        ) : (
          <div style={{ width: "100%", position: "relative" }}>
            {generatingReport && (
              <div className="report-generation-overlay">
                <div className="report-generation-content">
                  <div className="spinner-border text-primary report-generation-spinner" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="report-generation-text">Generating report, please wait...</div>
                </div>
              </div>
            )}
            <table className="table table-striped mt-3">
              <thead style={{ fontSize: "0.9rem" }}>
                <tr>
                  <th>S.no</th>
                  <th>Project name</th>
                  <th>Description</th>
                  <th>Expected duration years</th>
                  {/* <th>Expected duration months</th> */}
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {projectsList.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No projects found. Create your first value estimation project.
                    </td>
                  </tr>
                ) : (
                  projectsList.map((project, index) => (
                    <tr key={project.id || index}>
                      <td>{index + 1}</td>
                      <td>{project.projectName || 'Unnamed Project'}</td>
                      <td>{project.projectDescription || 'No description provided'}</td>
                      <td>{project.expectedDurationYears || 0}</td>
                      {/* <td>{project.expectedDurationMonths || 0}</td> */}
                      <td>
                        <span className={`badge ${getStatusBadgeClass(project.saveStage)}`}>
                          {project.saveStage || 'Draft'}
                        </span>
                      </td>
                      <td>
                        {project.saveStage !== 'Complete' && (
                          <button
                            className="btn btn-outline-primary btn-sm me-2"
                            onClick={() => handleEditProject(project)}
                            title="Edit Project"
                          >
                            <Pencil />
                          </button>
                        )}
                        {project.saveStage !== 'Draft' && (
                          <button
                            className="btn btn-outline-info btn-sm me-2"
                            onClick={() => handleViewProject(project)}
                            disabled={generatingReport}
                            title={generatingReport ? "Please wait..." : "View Project"}
                          >
                            <Eye />
                          </button>
                        )}
                        {project.saveStage === 'Complete' && (
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleViewReport(project)}
                            disabled={generatingReport}
                            title={generatingReport ? "Loading Report..." : "View Report"}
                          >
                            {generatingReport ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Loading...
                              </>
                            ) : (
                              <FileText />
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {(paginationInfo.totalPages > 1 || paginationInfo.totalRecords > 0) && (
              <div className="d-flex justify-content-center mt-4 mb-3">
                <nav aria-label="Projects pagination">
                  <ul className="pagination pagination-sm mb-0" style={{ 
                    borderRadius: '8px', 
                    padding: '8px',
                  }}>
                    <li className={`page-item ${!paginationInfo.hasPreviousPage ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => fetchProjectsList(paginationInfo.pageNumber - 1, paginationInfo.pageSize)}
                        disabled={!paginationInfo.hasPreviousPage}
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid #A72B2A',
                          color: '#A72B2A',
                          borderRadius: '6px',
                          margin: '0 2px',
                          padding: '6px 12px',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        Previous
                      </button>
                    </li>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, paginationInfo.totalPages) }, (_, i) => {
                      const startPage = Math.max(1, paginationInfo.pageNumber - 2);
                      const pageNum = startPage + i;
                      if (pageNum > paginationInfo.totalPages) return null;
                      
                      return (
                        <li key={pageNum} className={`page-item ${pageNum === paginationInfo.pageNumber ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => fetchProjectsList(pageNum, paginationInfo.pageSize)}
                            style={{
                              backgroundColor: pageNum === paginationInfo.pageNumber ? '#A72B2A' : 'transparent',
                              border: '1px solid #A72B2A',
                              color: pageNum === paginationInfo.pageNumber ? '#ffffff' : '#A72B2A',
                              borderRadius: '6px',
                              margin: '0 2px',
                              padding: '6px 12px',
                              fontSize: '14px',
                              fontWeight: '500',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (pageNum !== paginationInfo.pageNumber) {
                                e.target.style.backgroundColor = '#A72B2A';
                                e.target.style.color = '#ffffff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (pageNum !== paginationInfo.pageNumber) {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#A72B2A';
                              }
                            }}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    
                    <li className={`page-item ${!paginationInfo.hasNextPage ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => fetchProjectsList(paginationInfo.pageNumber + 1, paginationInfo.pageSize)}
                        disabled={!paginationInfo.hasNextPage}
                        style={{
                          backgroundColor: 'transparent',
                          border: '1px solid #A72B2A',
                          color: '#A72B2A',
                          borderRadius: '6px',
                          margin: '0 2px',
                          padding: '6px 12px',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <ClientAppShell
      title="Value Estimation"
      dataNav="business-value"
      selectedItem={selectedItem}
      onMenuSelect={handleMenuSelection}
      intro={{
        eyebrow: 'Business value',
        description:
          'Model costs, benefits, and ROI for your initiatives with structured inputs and summary insights you can share with stakeholders.',
      }}
    >
      <div className="dash-your-project-parent bvc-toolbar">
        <div className="dash-your-project">Your Project</div>
        <div className="view-toggle-buttons bvc-view-toggle">
          <button
            type="button"
            className={`action-button ${showListView ? "secondary" : "primary"}`}
            onClick={() => setShowListView(false)}
          >
            <Calculator /> Form View
          </button>
          <button
            type="button"
            className={`action-button ${showListView ? "primary" : "secondary"}`}
            onClick={() => setShowListView(true)}
          >
            <List /> List View
          </button>
        </div>
      </div>

      <div className="dash-frame-wrapper">
        <div className="dash-frame-div">
            <div className="business-calculator-container">
              {showReport ? (
                <BusinessValueReport
                  cashFlowReport={cashFlowReport}
                  formData={formData}
                  capabilities={capabilities}
                  expandedUseCases={expandedUseCases}
                  toggleUseCase={toggleUseCase}
                  businessValueProjectId={businessValueProjectId}
                  onClose={() => {
                    setShowReport(false);
                    setShowListView(true);
                  }}
                />
              ) : showSummary ? (
                renderSummaryView()
              ) : showListView ? (
                renderProjectsListView()
              ) : (
                <>
              
              {showProjectTypeSelection ? (
                renderProjectTypeSelection()
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className={`business-calculator-form ${isReadOnly ? 'read-only' : ''}`}
                  noValidate
                >
                  {/* Wizard header — inside form so it cannot block field interaction */}
                  <div className="bvc-wizard-header">
                    <button
                      type="button"
                      className="action-button secondary bvc-back-button"
                      onClick={handleBackToProjects}
                    >
                      <ArrowLeft /> Back
                    </button>
                    <div className="bvc-step-indicator-wrap">
                      <div className="step-indicator">
                        {[1, 2, 3, 4, 5, 6].map((step) => (
                          <div key={step} className={`step ${currentStep === step ? 'active' : ''} ${completedSteps.includes(step) ? 'completed' : ''}`}>
                            <div className="step-number">
                              {completedSteps.includes(step) ? <CheckCircle2 /> : step}
                            </div>
                            <div className="step-label">
                              {step === 1 && 'Project Scope'}
                              {step === 2 && 'Value Drivers'}
                              {step === 3 && 'Project Costs'}
                              {step === 4 && 'Value Questions'}
                              {step === 5 && 'Final Costs'}
                              {step === 6 && 'ROI Sensitivity'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-content-wrapper">
                    {renderCurrentStep()}
                  </div>

                  <div className="step-navigation-inline">
                      {currentStep > 1 && currentStep < 6 && (
                        <button
                          type="button"
                          onClick={prevStep}
                          className="nav-button prev"
                        >
                          <ArrowLeft />
                          Previous
                        </button>
                      )}
                      
                      {currentStep < 6 ? (
                        <button
                          type="button"
                          onClick={nextStep}
                          className="nav-button next"
                        >
                          Next
                          <ArrowRight />
                        </button>
                      ) : (
                        <div className="final-actions">
                          <button
                            type="button"
                            onClick={prevStep}
                            disabled={loading || currentStep <= 1}
                            className="action-button secondary"
                          >
                            <ArrowLeft />
                            Previous
                          </button>
                          <button
                            type="submit"
                            disabled={loading || isReadOnly}
                            className="action-button primary"
                          >
                            {loading && <span className="spinner"></span>}
                            <Save />
                            {loading ? 'Submitting...' : 'Submit'}
                          </button>
                        </div>
                      )}
                    </div>
                </form>
              )}
                </>
              )}
            </div>
          </div>
        </div>
    </ClientAppShell>
  );
};

export default BusinessValueCalculator;

